import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../services/api';
import { eventsService } from '../../services/eventsService';
import { userService } from '../../services/userService';
import { venuesService } from '../../services/venuesService';
import BooleanPill, { BooleanSelectEditor } from '../Shared/BooleanPill';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import UserLoginTooltip from '../Shared/UserLoginTooltip';
import VendorPlatformPill from '../Shared/VendorPlatformPill';
import YearSelector, { CURRENT_EVENT_YEAR, normalizeEventYear } from '../Shared/YearSelector';
import {
  applyNegativeAvailability,
  getUnmatchedSoldUnits,
  resolveAvailabilityCount,
} from './availabilityUtils';
import '../Shared/ResourceSidebar.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './EventsDisplayPage.css';
import {
  EVENT_CURRENCIES, decorateEventUnit, displayBuy, displaySell, originalSellPrice,
  currencyIssues, hasBlockedCurrency, currencyPriceTooltip, convertEventAdjustment, summarizeOriginalCurrencies,
  adjustmentOriginalCurrency,
} from './eventCurrency';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const USERNAME_COL_WIDTH = 78;
const DEFAULT_LISTING_STATUSES = ['REQUESTED', 'NEW', 'Listed', 'WL', 'RESCHED', 'REFUNDED', 'Complete', 'Cancelled'];
const DEFAULT_LISTING_MONITORS = ['Manual', 'Script', 'VisualPing'];
const DEFAULT_LISTING_SALE_POINTS = [
  'Skybox',
  'Stubhub',
  'Lysted',
  'HOLD',
  'AXS',
  'Shopify',
  'Stubhub LND',
  'Stubhub EU',
  'TM',
  'Ticketswap',
];
const EVENTS_POLL_INTERVAL_MS = 15000;
const PNL_ALLOWED_EMAILS = new Set(['masterte0@tikwiz.com', 'dimos@tikwiz.com']);
const TWZ_LOCATION_EDITOR_EMAIL = 'masterte0@tikwiz.com';
const SOLD_UNIT_RESTORE_EMAILS = new Set(['masterte0@tikwiz.com', 'dimos@tikwiz.com']);
const SHOW_GENERAL_VIEW_BUTTON = false;
const EVENT_TWZ_LOCATION_OPTIONS = ['New_York', 'North_America', 'World', 'Greece', 'London'];
const EMPTY_CREATE_VENUE_FORM = {
  name: '',
  location: '',
  country: '',
  capacity: '',
  twz_location: '',
};
const SEASON_TICKET_PACKAGES = [
  {
    id: 'ny-knicks-26-27',
    teamId: 'ny-knicks',
    name: 'NY Knicks',
    title: 'NY Knicks NBA Season Tickets',
    sourceTab: 'Season_Tickets',
    season: '2026-27',
    startDate: '2026-07-01',
    endDate: '2027-06-30',
    section: '314',
    row: '1',
    seats: 'S1-2',
    venue: 'MSG',
    location: 'NYC',
    platform: 'Ticketmaster',
  },
  {
    id: 'ny-knicks-25-26',
    teamId: 'ny-knicks',
    name: 'NY Knicks',
    title: 'NY Knicks NBA Season Tickets',
    sourceTab: 'Season_Tickets',
    season: '2025-26',
    startDate: '2025-07-01',
    endDate: '2026-06-30',
    section: '314',
    row: '1',
    seats: 'S1-2',
    venue: 'MSG',
    location: 'NYC',
    platform: 'Ticketmaster',
    games: 44,
    seatCount: 2,
    totalCost: 11176,
    pricePerGame: 127,
  },
  {
    id: 'miami-heat-26-27',
    teamId: 'miami-heat',
    name: 'Miami Heat',
    title: 'Miami Heat NBA Season Tickets',
    sourceTab: 'Season_Tickets',
    season: '2026-27',
    startDate: '2026-07-01',
    endDate: '2027-06-30',
    section: '117',
    row: '24',
    seats: '9-10',
    venue: 'Kaseya Center',
    location: 'Miami, FL',
    platform: 'Ticketmaster',
  },
];

const formatEventDate = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getDate()}-${MONTH_NAMES[date.getMonth()]}`;
};

const formatShopifyTitleDate = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  return `${weekday} ${String(date.getDate()).padStart(2, '0')} ${month}`;
};

const buildShopifyTitle = (row) => {
  const artist = String(row.artist || '').trim();
  const eventName = String(row.event_name || '').trim();
  const venue = String(row.venue || '').trim();
  const dateLabel = formatShopifyTitleDate(row.event_date);
  const names = eventName && eventName.toLowerCase() !== artist.toLowerCase()
    ? [artist, eventName]
    : [artist];
  let title = names.filter(Boolean).join(' | ');
  if (dateLabel) title += `${title ? ' - ' : ''}${dateLabel}`;
  if (venue) title += ` @ ${venue}`;
  return title;
};

const toCsvValue = (value) => {
  const text = value == null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadCsvFile = (fileName, rows) => {
  const csv = rows.map((row) => row.map(toCsvValue).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getExportFileDate = () => new Date().toISOString().slice(0, 10);

const EXPORT_VIEWS = ['listings', 'unit'];

// Columns left out of the event-level "CSV" export (position-level or layout-only columns).
const EVENT_CSV_EXCLUDED_FIELD = /^(month_label|adjustment_total|buy_ticket_\d+|sell_ticket_\d+)$/;

// Shopify smart collections are driven by these tags (Online Store > Collections).
const SHOPIFY_CITY_TAGS = {
  'new york': '1',
  miami: '2',
  aspen: '3',
  'las vegas': '4',
};

const SHOPIFY_IMAGE_URL = 'https://cdn.shopify.com/s/files/1/0730/3004/1909/files/profilpic_c7c99340-9d5e-4c86-9acf-baee7ef9d160.webp?v=1686253591';

const SHOPIFY_IMPORT_HEADER = [
  'Title', 'URL handle', 'Description', 'Vendor', 'Product category', 'Type', 'Tags',
  'Published on online store', 'Status', 'SKU', 'Option1 name', 'Option1 value', 'Price',
  'Compare-at price', 'Cost per item', 'Charge tax', 'Inventory tracker', 'Inventory quantity',
  'Continue selling when out of stock', 'Weight value (grams)', 'Weight unit for display',
  'Requires shipping', 'Fulfillment service', 'Product image URL', 'Image position',
  'Image alt text', 'Gift card',
];

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const toShopifyHandle = (title) => String(title || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const formatShopifyLongDate = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
};

const buildShopifyDescription = (row) => {
  const ticketType = String(row.listing_ticket_type || row.ticket_type || '').trim();
  const ticketLine = /^before/i.test(ticketType) ? `Entry ${ticketType}` : ticketType;
  const eventName = row.event_name || row.artist || '';
  const venue = row.venue || '';
  const city = row.location || '';
  const place = [venue, city].filter(Boolean).join(', ');
  const dateLabel = formatShopifyLongDate(row.event_date);
  return [
    `<h3>🎟️ General Admission tickets${ticketLine ? `<br><br>${escapeHtml(ticketLine)}` : ''}</h3>`,
    '<p> </p><h4><span style="text-decoration: underline;">Description</span></h4>',
    `<p>${escapeHtml(eventName)}${place ? ` at ${escapeHtml(place)}` : ''}${dateLabel ? ` on ${dateLabel}` : ''}.</p>`,
    place ? `<p>▸ ${escapeHtml(place)}</p>` : '',
    '<p><br>ℹ️  Tickets will be securely transferred to your DICE account.</p>',
  ].join('');
};

const buildShopifyImportRow = (row) => {
  const title = buildShopifyTitle(row);
  const city = String(row.location || '').trim().toLowerCase();
  return [
    title,
    toShopifyHandle(title),
    buildShopifyDescription(row),
    'Tikwiz',
    '',
    '',
    SHOPIFY_CITY_TAGS[city] || '',
    'TRUE',
    'draft',
    '',
    'Title',
    'Default Title',
    '',
    '',
    row.listing_avg_buy == null ? '' : Number(row.listing_avg_buy).toFixed(2),
    'FALSE',
    'shopify',
    parseAvailabilityCount(row.availability) ?? 0,
    'DENY',
    '0',
    'kg',
    'FALSE',
    'manual',
    SHOPIFY_IMAGE_URL,
    '1',
    title,
    'FALSE',
  ];
};

// Exports cover the rows on the current grid page (after filters and sorting).
const getCurrentPageRange = (api) => {
  const pageSize = api.paginationGetPageSize();
  const start = api.paginationGetCurrentPage() * pageSize;
  return { start, end: Math.min(start + pageSize, api.getDisplayedRowCount()) };
};

const getMonthLabel = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return MONTH_NAMES[date.getMonth()];
};

const getWeekdayLabel = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return WEEKDAY_LABELS[date.getDay()];
};

const getWeekdayClassName = (value) => {
  const label = getWeekdayLabel(value).toLowerCase();
  return label ? `events-dow-badge events-dow-${label}` : 'events-dow-badge';
};

const CALENDAR_WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromDateKey = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatRangeDate = (value) => {
  const date = fromDateKey(value);
  if (!date) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const getCalendarDays = (viewDate) => {
  const firstOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
  const firstCell = new Date(firstOfMonth);
  firstCell.setDate(firstCell.getDate() - mondayOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstCell);
    date.setDate(firstCell.getDate() + index);
    return date;
  });
};

const DateRangePicker = ({ value, onChange, legacyMonth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => fromDateKey(value.start) || new Date());
  const pickerRef = useRef(null);
  const calendarDays = useMemo(() => getCalendarDays(viewDate), [viewDate]);
  const todayKey = toDateKey(new Date());

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const selectRange = (startDate, endDate = startDate) => {
    onChange({ start: toDateKey(startDate), end: toDateKey(endDate) });
    setViewDate(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
  };

  const selectPreset = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (preset === 'today') {
      selectRange(today);
    } else if (preset === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      selectRange(yesterday);
    } else {
      const start = new Date(today);
      start.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      selectRange(start, end);
    }
    setIsOpen(false);
  };

  const selectDay = (date) => {
    const key = toDateKey(date);
    if (!value.start || value.end) {
      onChange({ start: key, end: '' });
      return;
    }

    onChange(key < value.start
      ? { start: key, end: value.start }
      : { start: value.start, end: key });
  };

  const selectWholeMonth = () => {
    const start = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const end = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    selectRange(start, end);
    setIsOpen(false);
  };

  const triggerText = value.start
    ? (value.end && value.end !== value.start
      ? `${formatRangeDate(value.start)} – ${formatRangeDate(value.end)}`
      : formatRangeDate(value.start))
    : (legacyMonth ? `${legacyMonth} (all years)` : 'All dates');

  return (
    <div className="events-date-picker" ref={pickerRef}>
      <button
        id="events-date-range"
        className={`events-date-trigger ${value.start || legacyMonth ? 'has-value' : ''}`}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className="events-date-trigger-icon" aria-hidden="true">▦</span>
        <span>{triggerText}</span>
        <span className="events-date-trigger-chevron" aria-hidden="true">⌄</span>
      </button>

      {isOpen && (
        <div className="events-calendar-popover" role="dialog" aria-label="Choose a date range">
          <div className="events-calendar-presets" aria-label="Quick date ranges">
            <button type="button" onClick={() => selectPreset('today')}>Today</button>
            <button type="button" onClick={() => selectPreset('yesterday')}>Yesterday</button>
            <button type="button" onClick={() => selectPreset('week')}>Current Week</button>
          </div>

          <div className="events-calendar-header">
            <button
              type="button"
              className="events-calendar-nav"
              aria-label="Previous month"
              onClick={() => setViewDate((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
            >
              ‹
            </button>
            <strong>{viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</strong>
            <button
              type="button"
              className="events-calendar-nav"
              aria-label="Next month"
              onClick={() => setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
            >
              ›
            </button>
          </div>

          <button type="button" className="events-calendar-month-action" onClick={selectWholeMonth}>
            Select entire month
          </button>

          <div className="events-calendar-grid">
            {CALENDAR_WEEKDAYS.map((day) => <span key={day} className="events-calendar-weekday">{day}</span>)}
            {calendarDays.map((date) => {
              const key = toDateKey(date);
              const isOutside = date.getMonth() !== viewDate.getMonth();
              const isEndpoint = key === value.start || key === value.end;
              const isInRange = value.start && value.end && key > value.start && key < value.end;
              return (
                <button
                  key={key}
                  type="button"
                  className={`events-calendar-day${isOutside ? ' outside' : ''}${isInRange ? ' in-range' : ''}${isEndpoint ? ' endpoint' : ''}${key === todayKey ? ' today' : ''}`}
                  aria-label={date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                  onClick={() => selectDay(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="events-calendar-footer">
            <span>{value.start && !value.end ? 'Now choose an end date' : 'Select any two dates for a range'}</span>
            <button type="button" onClick={() => { onChange({ start: '', end: '' }); setIsOpen(false); }}>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
};

const getEventGroupKey = (item) => item?.event_ref || item?.source_uuid || item?.event_id;

const comparePositionIdentifiers = (left, right) => (
  String(left?.source_uuid || left?.event_ref || left?.event_id || '').localeCompare(
    String(right?.source_uuid || right?.event_ref || right?.event_id || ''),
    undefined,
    { numeric: true, sensitivity: 'base' },
  )
);

const formatUnitPrice = (value) => {
  if (value == null || value === '') return '';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  return `${parsed.toFixed(2)}`;
};

const parsePnlNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isAvailableUnit = (unit) => String(unit?.status || '').trim().toLowerCase() === 'available';
const MOVE_TO_VOID_TARGET = '__void__';

const getUnitSourceLabel = (unit) => {
  if (unit?.user_name) return unit.user_name;
  if (unit?.user_email) return unit.user_email;
  if (unit?.purchase_id != null) return `Purchase ${unit.purchase_id}`;
  return 'VOID / Pending Purchase';
};

const createPnlAccumulator = () => ({
  buyTotal: 0,
  sellTotal: 0,
  units: 0,
  soldUnits: 0,
  buyCount: 0,
  sellCount: 0,
  adjustmentTotal: 0,
  buyBlocked: false,
  sellBlocked: false,
  adjustmentBlocked: false,
});

const getPnlSellPrice = (unit) => parsePnlNumber(displaySell(unit));

const calculateUnitViewPnl = (units, adjustmentTotal = 0) => (
  hasBlockedCurrency(units) ? null : (units || []).reduce((total, unit) => {
    const buyPrice = parsePnlNumber(displayBuy(unit)) || 0;
    const sellPrice = getPnlSellPrice(unit) || 0;
    return total + sellPrice - buyPrice;
  }, 0) + (parsePnlNumber(adjustmentTotal) || 0)
);

const createAdjustmentRow = (item = {}) => ({
  clientId: item.adjustment_id || `adjustment-${Date.now()}-${Math.random()}`,
  value: item.value == null ? '' : String(item.value),
  explanation: item.explanation || '',
});

const addUnitToPnl = (summary, unit) => {
  const buyPrice = parsePnlNumber(displayBuy(unit));
  const sellPrice = getPnlSellPrice(unit);

  summary.buyBlocked ||= Boolean(unit.currency_display?.buy.blocked);
  summary.sellBlocked ||= Boolean(unit.currency_display?.sell.blocked);
  summary.units += 1;
  if (originalSellPrice(unit) != null) summary.soldUnits += 1;
  if (buyPrice != null) {
    summary.buyTotal += buyPrice;
    summary.buyCount += 1;
  }
  if (sellPrice != null) {
    summary.sellTotal += sellPrice;
    summary.sellCount += 1;
  }
};

const mergePnlSummary = (target, source) => {
  target.buyBlocked ||= source.buyBlocked;
  target.sellBlocked ||= source.sellBlocked;
  target.adjustmentBlocked ||= source.adjustmentBlocked;
  target.adjustmentTotal += source.adjustmentTotal;
  target.buyTotal += source.buyTotal;
  target.sellTotal += source.sellTotal;
  target.units += source.units;
  target.soldUnits += source.soldUnits;
  target.buyCount += source.buyCount;
  target.sellCount += source.sellCount;
};

const finalizePnlSummary = (summary) => {
  // Adjustments (fees, refunds, credits entered per position) are part of the P/L.
  const profitLoss = summary.sellTotal - summary.buyTotal + summary.adjustmentTotal;
  const blocked = summary.buyBlocked || summary.sellBlocked || summary.adjustmentBlocked;
  return {
    pnl_adjustments: summary.adjustmentBlocked ? null : summary.adjustmentTotal,
    pnl_units: summary.units,
    pnl_sold_units: summary.soldUnits,
    pnl_avg_buy: !summary.buyBlocked && summary.buyCount ? summary.buyTotal / summary.buyCount : null,
    pnl_avg_sell: !summary.sellBlocked && summary.sellCount ? summary.sellTotal / summary.sellCount : null,
    pnl_profit_loss: !blocked && (summary.buyCount || summary.sellCount || summary.adjustmentTotal) ? profitLoss : null,
    pnl_roi: !blocked && summary.buyTotal > 0 ? (profitLoss / summary.buyTotal) * 100 : null,
  };
};

const formatPnlMoney = (value) => {
  const parsed = parsePnlNumber(value);
  if (parsed == null) return '';
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPnlPercent = (value) => {
  const parsed = parsePnlNumber(value);
  if (parsed == null) return '';
  return `${parsed.toFixed(1)}%`;
};

const getPnlToneClass = (value) => {
  const parsed = parsePnlNumber(value);
  if (parsed == null || parsed === 0) return 'events-pnl-neutral';
  return parsed > 0 ? 'events-pnl-positive' : 'events-pnl-negative';
};

const normalizeListingStatusKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-');

const formatListingStatus = (value) => {
  const key = normalizeListingStatusKey(value);
  if (key === 'requested') return 'REQUESTED';
  if (key === 'new') return 'NEW';
  if (key === 'listed') return 'Listed';
  if (key === 'wl') return 'WL';
  if (key === 'resched') return 'RESCHED';
  if (key === 'refunded') return 'REFUNDED';
  if (key === 'complete') return 'Complete';
  if (key === 'cancelled') return 'Cancelled';
  return value || '';
};

const getListingStatusClass = (value) => {
  const key = normalizeListingStatusKey(value);
  if (!key || key === 'multiple') return '';
  return `events-listing-status-cell events-listing-status-${key}`;
};

const getListingOperatorClass = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '';
  const classMap = {
    ts: 'events-listing-operator-cell events-listing-operator-ts',
    dv: 'events-listing-operator-cell events-listing-operator-dv',
    ria: 'events-listing-operator-cell events-listing-operator-ria',
    sai: 'events-listing-operator-cell events-listing-operator-sai',
    mk: 'events-listing-operator-cell events-listing-operator-mk',
  };
  return classMap[key] || 'events-listing-operator-cell';
};

const formatListingOperator = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (['admin', 'fun3s', 'john', 'jp', 'twz'].includes(key)) return '';
  return value || '';
};

const getListingUnitsLeftClass = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'events-listing-units-left-cell';
  if (parsed < 0) return 'events-listing-units-left-cell events-listing-units-left-negative';
  if (parsed === 0) return 'events-listing-units-left-cell events-listing-units-left-zero';
  return 'events-listing-units-left-cell events-listing-units-left-positive';
};

const getAvailabilityCellClass = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'events-availability-cell';
  if (parsed < 0) return 'events-availability-cell events-availability-negative';
  if (parsed === 0) return 'events-availability-cell events-availability-zero';
  return 'events-availability-cell events-availability-positive';
};

const normalizePnlKey = (value) => String(value || 'unknown')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '') || 'unknown';

const getBatchUnits = (unitMap, row) => {
  if (!row || row.row_type !== 'position') {
    return [];
  }

  const eventUnits = unitMap[row.event_ref]?.items || [];
  const batchRef = row.source_uuid || '';
  if (!batchRef) {
    return [];
  }

  return eventUnits
    .filter((unit) => unit.batch_code === batchRef)
    .sort((left, right) => (left.unit_number || 0) - (right.unit_number || 0));
};

const hasSellAssignment = (unit) => unit?.sell_unit_price != null || Boolean(unit?.tikwiz_order_id);

const getSellColumnUnits = (unitMap, row) => {
  const units = getBatchUnits(unitMap, row);
  const soldUnits = units.filter(hasSellAssignment);
  const openUnits = units.filter((unit) => !hasSellAssignment(unit));
  return [...soldUnits, ...openUnits];
};

// An order can be split across accounts: count its units on every position of the event, per holder.
const getOrderHolders = (unitMap, row, positions, orderId) => {
  const holders = new Map();
  (unitMap[row?.event_ref]?.items || [])
    .filter((unit) => String(unit?.tikwiz_order_id || '').trim() === orderId)
    .forEach((unit) => {
      const position = positions.find((item) => item.source_uuid === unit.batch_code);
      const key = String(position?.user_id || unit.batch_code || '');
      const holder = holders.get(key) || {
        userId: position?.user_id || null,
        username: position?.username || position?.assigned_user_raw || unit.batch_code || 'Unknown',
        quantity: 0,
      };
      holder.quantity += 1;
      holders.set(key, holder);
    });
  return Array.from(holders.values());
};

const getSoldOrderOptions = (unitMap, row, positions = []) => {
  const orders = new Map();
  getBatchUnits(unitMap, row).forEach((unit) => {
    const tikwizOrderId = String(unit?.tikwiz_order_id || '').trim();
    if (!tikwizOrderId) {
      return;
    }

    const displayOrderId = String(unit?.order_id || tikwizOrderId).trim();
    const current = orders.get(tikwizOrderId) || {
      orderId: tikwizOrderId,
      displayOrderId,
      quantity: 0,
      salePoint: unit.order_sale_point || '',
      ticketType: unit.type_label || getPositionTicketType(row),
    };
    current.quantity += 1;
    if (!current.displayOrderId && displayOrderId) {
      current.displayOrderId = displayOrderId;
    }
    if (!current.salePoint && unit.order_sale_point) {
      current.salePoint = unit.order_sale_point;
    }
    orders.set(tikwizOrderId, current);
  });

  orders.forEach((order) => {
    order.holders = getOrderHolders(unitMap, row, positions, order.orderId);
    if (order.holders.length) {
      order.quantity = order.holders.reduce((total, holder) => total + holder.quantity, 0);
    }
  });

  return Array.from(orders.values()).sort((left, right) => (
    (left.displayOrderId || left.orderId).localeCompare(right.displayOrderId || right.orderId)
  ));
};

const parseAvailabilityCount = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getPositionTicketType = (position, fallback = 'Unknown') => (
  String(position?.ticket_type || fallback || 'Unknown').trim() || 'Unknown'
);

const getUnitTicketType = (unit, ticketTypeByBatch, fallback = 'Unknown') => (
  String(
    unit?.type_label
      || ticketTypeByBatch.get(unit?.batch_code)
      || fallback
      || 'Unknown',
  ).trim() || 'Unknown'
);

const incrementCount = (map, key, count) => {
  map.set(key, (map.get(key) || 0) + count);
};

const buildAvailabilitySummary = (positions, eventUnits, ticketTypeByBatch, fallbackTicketType = 'Unknown') => {
  const byBatch = new Map();
  const byTicketType = new Map();
  const unmatchedSoldByBatch = new Map();
  const unmatchedSoldByTicketType = new Map();
  let total = null;

  if (eventUnits) {
    total = 0;
    eventUnits.forEach((unit) => {
      const ticketType = getUnitTicketType(unit, ticketTypeByBatch, fallbackTicketType);
      if (isAvailableUnit(unit)) {
        total += 1;
        incrementCount(byBatch, unit.batch_code, 1);
        incrementCount(byTicketType, ticketType, 1);
      }
    });

    const unmatchedSoldUnits = getUnmatchedSoldUnits(eventUnits);
    unmatchedSoldUnits.forEach((unit) => {
      const ticketType = getUnitTicketType(unit, ticketTypeByBatch, fallbackTicketType);
      incrementCount(unmatchedSoldByBatch, unit.batch_code, 1);
      incrementCount(unmatchedSoldByTicketType, ticketType, 1);
    });

    total = resolveAvailabilityCount(total, unmatchedSoldUnits.length);
    applyNegativeAvailability(byBatch, unmatchedSoldByBatch);
    applyNegativeAvailability(byTicketType, unmatchedSoldByTicketType);

    return { total, byBatch, byTicketType };
  }

  positions.forEach((position) => {
    const count = parseAvailabilityCount(position.availability);
    if (count == null) {
      return;
    }

    total = (total || 0) + count;
    incrementCount(byBatch, position.source_uuid, count);
    incrementCount(byTicketType, getPositionTicketType(position, fallbackTicketType), count);
  });

  return { total, byBatch, byTicketType };
};

const buildPnlUserBreakdown = (positions, eventUnits, ticketType, ticketTypeByBatch, fallbackTicketType = 'Unknown') => {
  const normalizedTicketType = String(ticketType || '').trim();
  const relevantBatchCodes = new Set();

  eventUnits.forEach((unit) => {
    if (getUnitTicketType(unit, ticketTypeByBatch, fallbackTicketType) === normalizedTicketType) {
      relevantBatchCodes.add(unit.batch_code);
    }
  });

  const matchingPositions = positions.filter((position) => (
    relevantBatchCodes.has(position.source_uuid)
      || getPositionTicketType(position, fallbackTicketType) === normalizedTicketType
  ));

  return matchingPositions.map((position) => {
    const batchUnits = eventUnits.filter((unit) => unit.batch_code === position.source_uuid);
    const availableFallback = parseAvailabilityCount(position.availability);
    return {
      batchCode: position.source_uuid || position.event_ref || '',
      userId: position.user_id || null,
      username: position.username || position.assigned_user_raw || 'Unassigned',
      vendor: position.vendor || '',
      platform: position.platform || '',
      available: batchUnits.length
        ? batchUnits.filter(isAvailableUnit).length
        : (availableFallback ?? 0),
      sold: batchUnits.filter((unit) => originalSellPrice(unit) != null).length,
      units: batchUnits.length,
    };
  });
};

const normalizeListingTicketType = (value) => String(value || 'Unknown').trim() || 'Unknown';

const buildListingKey = (eventRef, ticketType, slot) => [
  String(eventRef || '').trim(),
  normalizeListingTicketType(ticketType),
  String(slot || '').trim().toLowerCase(),
].join('||');

const buildTransferKey = (eventRef, ticketType) => [
  String(eventRef || '').trim(),
  normalizeListingTicketType(ticketType),
].join('||');

const averageUnitBuy = (units) => {
  if (hasBlockedCurrency(units, 'buy')) return null;
  const prices = units
    .map((unit) => parsePnlNumber(displayBuy(unit)))
    .filter((value) => value != null);
  if (!prices.length) return null;
  return prices.reduce((total, value) => total + value, 0) / prices.length;
};

const summarizeTransferQuantity = (transfers) => (
  transfers.reduce((total, transfer) => total + (Number(transfer.quantity) || 0), 0)
);

const getListingNumber = (listing, field) => {
  const parsed = Number(listing?.[field]);
  return Number.isFinite(parsed) ? parsed : 0;
};

const renderUnitPriceCell = (price, meta, className = '') => {
  if (price == null) return '';

  return React.createElement(
    'div',
    { className: `events-ticket-card ${className}`.trim() },
    React.createElement('span', { className: 'events-ticket-price' }, formatUnitPrice(price)),
  );
};

const selectVenueNames = (venues) => {
  const seen = new Set();
  return (venues || []).reduce((options, venue) => {
    const name = String(venue?.name || '').trim();
    const key = name.toLowerCase();
    if (name && !seen.has(key)) {
      seen.add(key);
      options.push(name);
    }
    return options;
  }, []);
};

const selectVendorLabels = (vendors) => {
  const seen = new Set();
  return (vendors || []).reduce((options, vendor) => {
    const name = String(vendor || '').trim();
    const key = name.toLowerCase();
    if (name && !seen.has(key)) {
      seen.add(key);
      options.push(name);
    }
    return options;
  }, []);
};

const findVendorLabel = (vendors, value) => {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '';
  return selectVendorLabels(vendors).find((vendor) => vendor.toLowerCase() === key) || null;
};

const findVenueOption = (venues, value) => {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return null;
  return (venues || []).find((venue) => String(venue?.name || '').trim().toLowerCase() === key) || null;
};

const VenueSearchEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value || '');
  const inputRef = useRef(null);
  const options = selectVenueNames(props.values || []);
  const listId = `events-venue-options-${props.node?.id || 'editor'}`;

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    afterGuiAttached: () => {
      inputRef.current?.focus();
      inputRef.current?.select();
    },
  }));

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const updateValue = (nextValue) => {
    setValue(nextValue);
    props.onValueChange?.(nextValue);
  };

  const commitCurrentValue = () => {
    const nextValue = inputRef.current?.value ?? value;
    updateValue(nextValue);
    window.setTimeout(() => props.stopEditing?.(), 0);
  };

  return (
    <>
      <input
        ref={inputRef}
        className="events-venue-editor"
        list={listId}
        value={value}
        onInput={(event) => updateValue(event.target.value)}
        onChange={(event) => updateValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.stopPropagation();
            commitCurrentValue();
            return;
          }
          if (event.key === 'Escape') {
            event.stopPropagation();
            props.stopEditing?.(true);
            return;
          }
          event.stopPropagation();
        }}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </>
  );
});

VenueSearchEditor.displayName = 'VenueSearchEditor';

const VendorTypeaheadEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value || '');
  const inputRef = useRef(null);
  const options = selectVendorLabels(props.values || []);
  const listId = `events-vendor-options-${props.node?.id || 'editor'}`;

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    afterGuiAttached: () => {
      inputRef.current?.focus();
      inputRef.current?.select();
    },
  }));

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const updateValue = (nextValue) => {
    setValue(nextValue);
    props.onValueChange?.(nextValue);
  };

  const commitCurrentValue = () => {
    const nextValue = inputRef.current?.value ?? value;
    updateValue(nextValue);
    window.setTimeout(() => props.stopEditing?.(), 0);
  };

  return (
    <>
      <input
        ref={inputRef}
        className="events-vendor-editor"
        list={listId}
        value={value}
        onInput={(event) => updateValue(event.target.value)}
        onChange={(event) => updateValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.stopPropagation();
            commitCurrentValue();
            return;
          }
          if (event.key === 'Escape') {
            event.stopPropagation();
            props.stopEditing?.(true);
            return;
          }
          event.stopPropagation();
        }}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </>
  );
});

VendorTypeaheadEditor.displayName = 'VendorTypeaheadEditor';

const renderSoldOutPill = (value) => {
  return <BooleanPill value={value} />;
};

const EventDetailsToggleFloatingFilter = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    onParentModelChanged: () => {},
  }));

  return (
    <button
      type="button"
      className="events-details-toggle-filter"
      onClick={(event) => {
        event.stopPropagation();
        props.onToggle?.();
      }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <span>Details</span>
      <span className="events-details-toggle-arrow">{props.expanded ? 'v' : '>'}</span>
    </button>
  );
});

EventDetailsToggleFloatingFilter.displayName = 'EventDetailsToggleFloatingFilter';

const getUserLoginTooltipValue = (params) => {
  const row = params.data;
  if (!row || row.row_type === 'pnl_month_summary') return null;
  const entries = row.row_type === 'pnl' && row.pnl_user_positions?.length
    ? row.pnl_user_positions
    : row.user_login_entries;
  return entries?.length ? entries : null;
};

const ListingsTransferTooltip = (props) => {
  const transfers = Array.isArray(props.value) ? props.value : [];

  return (
    <div className="events-listings-transfer-tooltip">
      <div className="events-listings-transfer-tooltip-title">Transfers</div>
      {!transfers.length ? (
        <div className="events-listings-transfer-empty">No transfers recorded.</div>
      ) : (
        <div className="events-listings-transfer-list">
          {transfers.map((transfer) => (
            <div
              key={transfer.transfer_id || `${transfer.operator}-${transfer.transfer_username}-${transfer.quantity}`}
              className="events-listings-transfer-row"
            >
              <span className="events-transfer-order-id">
                {transfer.order_id ? `Order ${transfer.order_id}` : 'Manual'}
              </span>
              <span>{transfer.transfer_username || 'Unknown user'}</span>
              <span>{transfer.operator}</span>
              <strong>{transfer.quantity}</strong>
            </div>
          ))}
        </div>
      )}
      <div className="events-listings-transfer-hint">Click the cell to view transfer details.</div>
    </div>
  );
};

const createBatchDetailsState = (row, batchUnits) => {
  if (!row || row.row_type !== 'position') {
    return null;
  }

  return {
    eventName: row.event_name || '',
    eventDate: row.event_date || null,
    venue: row.venue || '',
    vendor: row.vendor || '',
    platform: row.platform || '',
    eventRef: row.event_ref || '',
    batchCode: row.source_uuid || '',
    items: batchUnits,
  };
};

const createUserHistoryState = (row, result) => ({
  eventName: row.event_name || '',
  username: result.username || row.username || '',
  assignedUserRaw: result.assigned_user_raw || row.assigned_user_raw || '',
  futureItems: result.future_items || [],
  pastItems: result.past_items || [],
  futureCount: result.future_count || 0,
  pastCount: result.past_count || 0,
});

const SeasonTicketsSidebar = ({ packages, selectedPackageId, onSelectPackage }) => {
  const selectedPackage = packages.find((item) => item.id === selectedPackageId) || packages[0];

  if (!selectedPackage) {
    return null;
  }

  const teams = packages.reduce((result, item) => {
    if (!result.some((team) => team.id === item.teamId)) {
      result.push({ id: item.teamId, name: item.name });
    }
    return result;
  }, []);
  const selectedTeamPackages = packages.filter((item) => item.teamId === selectedPackage.teamId);
  const detailItems = [
    ['Season', selectedPackage.season],
    ['Section', selectedPackage.section],
    ['Row', selectedPackage.row],
    ['Seats', selectedPackage.seats],
    ['Venue', selectedPackage.venue],
    ['Location', selectedPackage.location],
    ['Platform', selectedPackage.platform],
  ];

  return (
    <aside className="resource-sidebar events-season-resource-sidebar">
      <nav className="resource-sidebar-nav events-season-resource-nav" aria-label="Season ticket teams">
        <section className="resource-sidebar-section">
          <div className="resource-sidebar-heading">Teams</div>
          <div className="resource-sidebar-children">
        {teams.map((team) => (
          <button
            key={team.id}
            type="button"
                className={`resource-sidebar-link resource-sidebar-child ${team.id === selectedPackage.teamId ? 'active' : ''}`}
            onClick={() => onSelectPackage(packages.find((item) => item.teamId === team.id)?.id)}
          >
            {team.name}
          </button>
        ))}
          </div>
        </section>
        <section className="resource-sidebar-section">
          <div className="resource-sidebar-heading">Seasons</div>
          <div className="resource-sidebar-children">
        {selectedTeamPackages.map((item) => (
          <button
            key={item.id}
            type="button"
                className={`resource-sidebar-link resource-sidebar-child ${item.id === selectedPackage.id ? 'active' : ''}`}
            onClick={() => onSelectPackage(item.id)}
          >
            {item.season}
          </button>
        ))}
          </div>
        </section>
        <section className="resource-sidebar-section events-season-team-details">
          <div className="resource-sidebar-heading">Details</div>
        {detailItems.map(([label, value]) => (
          <div key={label} className="events-season-team-detail-row">
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
        </section>
      </nav>
    </aside>
  );
};

ModuleRegistry.registerModules([AllCommunityModule]);

const EventsDisplayPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialQuery = useMemo(() => new URLSearchParams(location.search).get('q') || '', [location.search]);
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get('tab') || '');
  const [query, setQuery] = useState(initialQuery);
  const [selectedYear, setSelectedYear] = useState(() => (
    normalizeEventYear(new URLSearchParams(location.search).get('year'))
  ));
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  const [monthFilter, setMonthFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [activeView, setActiveView] = useState('unit');
  const gridRef = useRef(null);
  const [showSeasonTickets, setShowSeasonTickets] = useState(false);
  const [selectedSeasonPackageId, setSelectedSeasonPackageId] = useState(SEASON_TICKET_PACKAGES[0].id);
  const [expandedEventIds, setExpandedEventIds] = useState(() => new Set());
  const [eventDetailsExpanded, setEventDetailsExpanded] = useState(true);
  const [collapsedPnlMonths, setCollapsedPnlMonths] = useState(() => new Set());
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState('');
  const [rawUnitMap, setUnitMap] = useState({});
  const [currencySnapshot, setCurrencySnapshot] = useState(null);
  const [currencyPanel, setCurrencyPanel] = useState(null);
  const isCurrencyLocationView = !location.pathname.startsWith('/dashboard') && !showSeasonTickets;
  const displayCurrency = isCurrencyLocationView && !['unit', 'listings'].includes(activeView)
    ? (tab ? EVENT_CURRENCIES[tab] : 'USD') : null;
  const unitMap = useMemo(() => {
    if (!isCurrencyLocationView) return rawUnitMap;
    const batchCurrencies = new Map(items
      .filter((item) => !tab || (item.twz_location || item.source_tab) === tab)
      .map((item) => [item.source_uuid, EVENT_CURRENCIES[item.twz_location || item.source_tab] || null]));
    return Object.fromEntries(Object.entries(rawUnitMap).map(([ref, result]) => [ref, {
      ...result,
      items: (result.items || [])
        .filter((unit) => batchCurrencies.has(unit.batch_code))
        .map((unit) => decorateEventUnit(unit, displayCurrency, currencySnapshot, batchCurrencies.get(unit.batch_code))),
    }]));
  }, [rawUnitMap, displayCurrency, currencySnapshot, items, tab, isCurrencyLocationView]);

  useEffect(() => {
    if (!displayCurrency) return undefined;
    let cancelled = false;
    eventsService.getCurrencyRates().then((result) => {
      if (!cancelled) setCurrencySnapshot(result.success ? result : null);
    });
    return () => { cancelled = true; };
  }, [displayCurrency]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingsError, setListingsError] = useState('');
  const [listingMap, setListingMap] = useState({});
  const [transferMap, setTransferMap] = useState({});
  const [listingOptions, setListingOptions] = useState({
    statuses: DEFAULT_LISTING_STATUSES,
    monitors: DEFAULT_LISTING_MONITORS,
    salePoints: DEFAULT_LISTING_SALE_POINTS,
    operators: [],
    transferUsers: [],
  });
  const [venueOptions, setVenueOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [transferPanel, setTransferPanel] = useState(null);
  const [transferForm, setTransferForm] = useState({
    quantity: 1,
    operator: '',
    transferUserId: '',
  });
  const [detailsPanel, setDetailsPanel] = useState(null);
  const [restoringUnitId, setRestoringUnitId] = useState('');
  const [adjustmentPanel, setAdjustmentPanel] = useState(null);
  const [adjustmentRows, setAdjustmentRows] = useState([]);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [adjustmentSaving, setAdjustmentSaving] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState('');
  const [moveOrderPanel, setMoveOrderPanel] = useState(null);
  const [moveOrderForm, setMoveOrderForm] = useState({
    orderId: '',
    targetUserId: '',
    allowOverAvailability: false,
  });
  const [moveOrderWarning, setMoveOrderWarning] = useState('');
  const [moveOrderSaving, setMoveOrderSaving] = useState(false);
  const [historyPanel, setHistoryPanel] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [eventLinkPanel, setEventLinkPanel] = useState(null);
  const [eventLinkValue, setEventLinkValue] = useState('');
  const [eventLinkError, setEventLinkError] = useState('');
  const [eventLinkSaving, setEventLinkSaving] = useState(false);
  const [addPositionPanel, setAddPositionPanel] = useState(null);
  const [addPositionForm, setAddPositionForm] = useState({
    isVoid: false,
    userId: '',
    quantity: '1',
    unitPrice: '0.00',
  });
  const [addPositionUserQuery, setAddPositionUserQuery] = useState('');
  const [addPositionUserOptions, setAddPositionUserOptions] = useState([]);
  const [addPositionLoading, setAddPositionLoading] = useState(false);
  const [addPositionPrefillLoading, setAddPositionPrefillLoading] = useState(false);
  const [addPositionSearching, setAddPositionSearching] = useState(false);
  const [addPurchasePanel, setAddPurchasePanel] = useState(null);
  const [addPurchaseForm, setAddPurchaseForm] = useState({
    quantity: '1',
    unitPrice: '',
    card: '',
  });
  const [addPurchaseLoading, setAddPurchaseLoading] = useState(false);
  const [showCreateVenueModal, setShowCreateVenueModal] = useState(false);
  const [createVenueForm, setCreateVenueForm] = useState(EMPTY_CREATE_VENUE_FORM);
  const [createVenueError, setCreateVenueError] = useState('');
  const [createVenueSaving, setCreateVenueSaving] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const pollRefreshInFlightRef = useRef(false);
  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const canSeePnl = isDashboardRoute || PNL_ALLOWED_EMAILS.has(currentUserEmail);
  const canEditTwzLocation = currentUserEmail === TWZ_LOCATION_EDITOR_EMAIL;
  const canRestoreSoldUnits = SOLD_UNIT_RESTORE_EMAILS.has(currentUserEmail);
  const selectedSeasonPackage = useMemo(
    () => SEASON_TICKET_PACKAGES.find((item) => item.id === selectedSeasonPackageId) || SEASON_TICKET_PACKAGES[0],
    [selectedSeasonPackageId],
  );

  useEffect(() => {
    let isMounted = true;
    const token = authService.getToken();
    if (!token) {
      return () => {
        isMounted = false;
      };
    }

    authService.getCurrentUser(token)
      .then((user) => {
        if (!isMounted) return;
        setCurrentUserEmail(String(user?.email || '').trim().toLowerCase());
      })
      .catch(() => {
        if (isMounted) {
          setCurrentUserEmail('');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!canSeePnl && activeView === 'pnl') {
      setActiveView('general');
    }
  }, [activeView, canSeePnl]);

  const refreshVenueOptions = useCallback(async () => {
    const result = await venuesService.listVenues({ limit: 5000 });
    if (!result.success) {
      return result;
    }
    setVenueOptions(result.items || []);
    return result;
  }, []);

  useEffect(() => {
    let isMounted = true;

    refreshVenueOptions().then((result) => {
      if (!isMounted || !result?.success) return;
      setVenueOptions(result.items || []);
    });

      eventsService.listVendors().then((result) => {
        if (!isMounted) return;
        if (result.success) {
          setVendorOptions(result.items || []);
        }
      });

      eventsService.listPlatforms().then((result) => {
        if (!isMounted) return;
        if (result.success) {
          setPlatformOptions(result.items || []);
        }
      });

      eventsService.getListingOptions().then((result) => {
      if (!isMounted) return;
      if (result.success) {
        setListingOptions({
          statuses: result.statuses?.length ? result.statuses : DEFAULT_LISTING_STATUSES,
          monitors: result.monitors?.length ? result.monitors : DEFAULT_LISTING_MONITORS,
          salePoints: result.sale_points?.length ? result.sale_points : DEFAULT_LISTING_SALE_POINTS,
          operators: result.operators || [],
          transferUsers: result.transfer_users || [],
        });
      }
    });

    return () => {
      isMounted = false;
    };
  }, [refreshVenueOptions]);

  useEffect(() => {
    if (!addPositionPanel || addPositionForm.isVoid) {
      setAddPositionUserOptions([]);
      setAddPositionSearching(false);
      return undefined;
    }

    const searchText = addPositionUserQuery.trim();
    if (searchText.length < 1) {
      setAddPositionUserOptions([]);
      setAddPositionSearching(false);
      return undefined;
    }

    let isMounted = true;
    setAddPositionSearching(true);
    const timeout = window.setTimeout(async () => {
      const result = await userService.searchVendorUsers(
        searchText,
        addPositionPanel.vendor || addPositionPanel.platform || undefined,
      );
      if (!isMounted) return;
      setAddPositionSearching(false);
      setAddPositionUserOptions(result.success ? (result.items || []) : []);
    }, 180);

    return () => {
      isMounted = false;
      window.clearTimeout(timeout);
    };
  }, [addPositionForm.isVoid, addPositionPanel, addPositionUserQuery]);

  const toggleEventDetails = useCallback(() => {
    setEventDetailsExpanded((current) => !current);
  }, []);

  const closeCreateVenueModal = useCallback(() => {
    if (createVenueSaving) return;
    setShowCreateVenueModal(false);
    setCreateVenueError('');
  }, [createVenueSaving]);

  const updateCreateVenueField = useCallback((field, value) => {
    setCreateVenueForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleCreateVenue = useCallback(async (event) => {
    event.preventDefault();

    const name = createVenueForm.name.trim();
    const venueLocation = createVenueForm.location.trim();
    const country = createVenueForm.country.trim();
    if (!name || !venueLocation || !country) {
      setCreateVenueError('Venue name, Location, and Country are required.');
      return;
    }

    const capacity = createVenueForm.capacity === '' ? null : Number(createVenueForm.capacity);
    if (capacity != null && (Number.isNaN(capacity) || capacity < 0)) {
      setCreateVenueError('Capacity must be a nonnegative number.');
      return;
    }

    setCreateVenueSaving(true);
    setCreateVenueError('');
    const result = await venuesService.createVenue({
      name,
      location: venueLocation,
      country,
      capacity: capacity == null ? null : Math.trunc(capacity),
      twz_location: createVenueForm.twz_location || null,
    });
    setCreateVenueSaving(false);

    if (!result.success) {
      setCreateVenueError(result.error);
      return;
    }

    const refreshResult = await refreshVenueOptions();
    if (!refreshResult?.success) {
      setError(refreshResult?.error || 'Venue was created, but the venue list could not be refreshed.');
    } else {
      setError('');
    }
    setMessage(`Venue "${result.item?.name || name}" created. You can now select it in Events.`);
    setShowCreateVenueModal(false);
  }, [createVenueForm, refreshVenueOptions]);

  const soldOutColumn = useMemo(() => ({
    headerName: 'Sold Out',
    field: 'sold_out',
    minWidth: 112,
    width: 112,
    maxWidth: 112,
    editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
    singleClickEdit: true,
    cellEditor: BooleanSelectEditor,
    cellRenderer: (params) => {
      if (params.data?.row_type === 'pnl_month_summary') {
        return '';
      }
      return renderSoldOutPill(params.value);
    },
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponent: EventDetailsToggleFloatingFilter,
    floatingFilterComponentParams: {
      expanded: eventDetailsExpanded,
      onToggle: toggleEventDetails,
    },
    suppressFloatingFilterButton: true,
  }), [eventDetailsExpanded, toggleEventDetails]);

  const readonlySoldOutColumn = useMemo(() => ({
    ...soldOutColumn,
    editable: false,
    singleClickEdit: false,
    cellEditor: undefined,
  }), [soldOutColumn]);

  const eventDetailColumnsBeforeSoldOut = useMemo(() => (
    eventDetailsExpanded
      ? [
        {
          headerName: 'Venue',
          field: 'venue',
          minWidth: 180,
          width: 180,
          flex: 1,
          editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
          singleClickEdit: true,
          cellEditor: VenueSearchEditor,
          cellEditorPopup: true,
          cellEditorParams: {
            values: venueOptions,
          },
        },
        { headerName: 'Location', field: 'location', minWidth: 120, width: 120, editable: false },
        {
          headerName: 'Vendor',
          field: 'vendor',
          minWidth: 120,
          width: 120,
          editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
          singleClickEdit: true,
          cellEditor: VendorTypeaheadEditor,
          cellEditorPopup: true,
          cellEditorParams: {
            values: vendorOptions,
          },
          cellRenderer: (params) => <VendorPlatformPill kind="vendor" value={params.value} />,
        },
        {
          headerName: 'Platform',
          field: 'platform',
          minWidth: 120,
          width: 120,
          editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
          singleClickEdit: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: selectVendorLabels(platformOptions),
          },
          cellRenderer: (params) => <VendorPlatformPill kind="platform" value={params.value} />,
        },
      ]
      : []
  ), [eventDetailsExpanded, platformOptions, venueOptions, vendorOptions]);

  const eventDetailColumnsAfterSoldOut = useMemo(() => (
    eventDetailsExpanded
      ? [
        {
          headerName: 'Event Name',
          field: 'event_name',
          minWidth: 180,
          flex: 1.3,
          editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
        },
      ]
      : []
  ), [eventDetailsExpanded]);

  const readonlyEventDetailColumnsBeforeSoldOut = useMemo(() => (
    eventDetailsExpanded
      ? [
        { headerName: 'Venue', field: 'venue', minWidth: 170, width: 180, flex: 1, editable: false },
        { headerName: 'Location', field: 'location', minWidth: 120, width: 120, editable: false },
        {
          headerName: 'Vendor',
          field: 'vendor',
          minWidth: 120,
          width: 120,
          editable: false,
          cellRenderer: (params) => <VendorPlatformPill kind="vendor" value={params.value} />,
        },
        {
          headerName: 'Platform',
          field: 'platform',
          minWidth: 120,
          width: 120,
          editable: false,
          cellRenderer: (params) => <VendorPlatformPill kind="platform" value={params.value} />,
        },
      ]
      : []
  ), [eventDetailsExpanded]);

  const readonlyEventDetailColumnsAfterSoldOut = useMemo(() => (
    eventDetailsExpanded
      ? [
        { headerName: 'Event Name', field: 'event_name', minWidth: 180, flex: 1.3, editable: false },
      ]
      : []
  ), [eventDetailsExpanded]);

  const pnlEventDetailColumnsAfterSoldOut = useMemo(() => (
    readonlyEventDetailColumnsAfterSoldOut.filter((column) => column.field !== 'secondary_artist')
  ), [readonlyEventDetailColumnsAfterSoldOut]);

  const baseColumnDefs = useMemo(() => [
    {
      headerName: 'Month',
      field: 'month_label',
      pinned: 'left',
      minWidth: 70,
      maxWidth: 70,
      sortable: false,
      filter: false,
      floatingFilter: false,
      editable: false,
      cellClass: 'events-month-cell',
      cellRenderer: (params) => (params.data?.month_break ? params.value : ''),
    },
    {
      headerName: 'ID',
      field: 'display_id',
      pinned: 'left',
      minWidth: 120,
      maxWidth: 120,
      sortable: false,
      filter: false,
      floatingFilter: false,
      editable: false,
      cellClass: (params) => (
        params.data?.row_type === 'event'
          ? 'events-id-cell events-parent-id-cell'
          : 'events-id-cell events-position-id-cell'
      ),
    },
    {
      headerName: 'Day',
      field: 'weekday_label',
      pinned: 'left',
      minWidth: 88,
      maxWidth: 88,
      sortable: false,
      filter: false,
      floatingFilter: false,
      editable: false,
      cellClass: 'events-dow-cell',
      cellRenderer: (params) => {
        if (!params.value) return '';
        return React.createElement(
          'span',
          { className: getWeekdayClassName(params.data?.event_date) },
          params.value,
        );
      },
    },
    {
      headerName: 'Date',
      field: 'event_date',
      pinned: 'left',
      minWidth: 100,
      maxWidth: 100,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => formatEventDate(params.value),
    },
    {
      headerName: 'Artist',
      field: 'artist',
      pinned: 'left',
      minWidth: 135,
      width: 150,
      maxWidth: 190,
      editable: (params) => params.data?.row_type === 'position',
      cellClass: (params) => (
        !isDashboardRoute
        && params.data?.row_type === 'event'
        && String(params.data?.primary_event_link || '').trim()
          ? 'events-linked-artist-cell'
          : ''
      ),
    },
    {
      headerName: 'Ticket Type',
      field: 'ticket_type',
      minWidth: 130,
      width: 140,
      editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
      cellClass: (params) => (
        String(params.value || '').trim().toUpperCase() === 'VOID'
          ? 'events-ticket-type-void-cell'
          : ''
      ),
    },
    {
      headerName: 'Availability',
      field: 'availability',
      minWidth: 105,
      maxWidth: 115,
      editable: false,
      filter: 'agNumberColumnFilter',
      cellClass: (params) => getAvailabilityCellClass(params.value),
      valueFormatter: (params) => (params.value == null ? '' : params.value),
    },
    {
      headerName: 'User',
      field: 'username',
      minWidth: USERNAME_COL_WIDTH,
      width: USERNAME_COL_WIDTH,
      maxWidth: USERNAME_COL_WIDTH,
      suppressFloatingFilterButton: true,
      tooltipComponent: UserLoginTooltip,
      tooltipValueGetter: getUserLoginTooltipValue,
    },
    ...eventDetailColumnsBeforeSoldOut,
    soldOutColumn,
    ...eventDetailColumnsAfterSoldOut,
  ], [eventDetailColumnsAfterSoldOut, eventDetailColumnsBeforeSoldOut, isDashboardRoute, soldOutColumn]);

  const defaultColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  };

  const localFilteredItems = useMemo(() => {
    if (!showSeasonTickets || !selectedSeasonPackage) {
      return isCurrencyLocationView && tab
        ? items.filter((item) => (item.twz_location || item.source_tab) === tab)
        : items;
    }

    const selectedTeam = String(selectedSeasonPackage.name || '').trim().toLowerCase();
    const search = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesTeam = String(item.artist || '').trim().toLowerCase() === selectedTeam;
      const eventDate = String(item.event_date || '').slice(0, 10);
      const matchesSeason = eventDate
        && eventDate >= selectedSeasonPackage.startDate
        && eventDate <= selectedSeasonPackage.endDate;
      if (!matchesTeam || !matchesSeason) {
        return false;
      }
      if (!search) {
        return true;
      }
      return [
        item.event_name,
        item.artist,
        item.venue,
        item.location,
        item.ticket_type,
        item.platform,
        item.vendor,
        item.source_uuid,
        item.event_ref,
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [items, query, selectedSeasonPackage, showSeasonTickets, isCurrencyLocationView, tab]);

  const filteredItems = useMemo(() => {
    if (!upcomingOnly) {
      return localFilteredItems;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return localFilteredItems.filter((item) => {
      if (!item.event_date) {
        return false;
      }

      const eventDate = new Date(`${item.event_date}T00:00:00`);
      return !Number.isNaN(eventDate.getTime()) && eventDate >= today;
    });
  }, [localFilteredItems, upcomingOnly]);

  const monthFilteredItems = useMemo(() => {
    if (!monthFilter && !dateRange.start) {
      return filteredItems;
    }

    return filteredItems.filter((item) => {
      if (monthFilter && getMonthLabel(item.event_date) !== monthFilter) return false;
      if (!dateRange.start) return true;
      if (!item.event_date || item.event_date < dateRange.start) return false;
      return item.event_date <= (dateRange.end || dateRange.start);
    });
  }, [dateRange, filteredItems, monthFilter]);

  const groupedEvents = useMemo(() => {
    const groups = [];
    const byEvent = new Map();

    monthFilteredItems.forEach((item) => {
      const eventKey = getEventGroupKey(item);
      if (!byEvent.has(eventKey)) {
        byEvent.set(eventKey, []);
        groups.push({ eventKey, positions: byEvent.get(eventKey) });
      }
      byEvent.get(eventKey).push(item);
    });

    let previousMonthKey = null;

    return groups.map(({ eventKey, positions }) => {
      const sortedPositions = [...positions].sort(comparePositionIdentifiers);
      const first = sortedPositions[0];
      const distinctTicketTypes = Array.from(new Set(
        sortedPositions
          .map((position) => (position.ticket_type || '').trim())
          .filter(Boolean),
      ));
      const distinctVendors = Array.from(new Set(
        sortedPositions
          .map((position) => (position.vendor || '').trim())
          .filter(Boolean),
      ));
      const distinctLocations = Array.from(new Set(
        sortedPositions
          .map((position) => (position.location || '').trim())
          .filter(Boolean),
      ));
      const distinctTwzLocations = Array.from(new Set(
        sortedPositions
          .map((position) => (position.twz_location || '').trim())
          .filter(Boolean),
      ));
      const distinctSecondaryArtists = Array.from(new Set(
        sortedPositions
          .map((position) => (position.secondary_artist || '').trim())
          .filter(Boolean),
      ));
      const distinctListingStatuses = Array.from(new Set(
        sortedPositions
          .map((position) => (position.listing_status || '').trim())
          .filter(Boolean),
      ));
      const monthLabel = getMonthLabel(first?.event_date);
      const monthKey = first?.event_date ? first.event_date.slice(0, 7) : '';
      const monthBreak = Boolean(monthKey) && monthKey !== previousMonthKey;
      const eventRef = first?.event_ref || eventKey;
      const fallbackTicketType = distinctTicketTypes[0] || 'Unknown';
      const ticketTypeByBatch = new Map(
        sortedPositions.map((position) => [
          position.source_uuid,
          getPositionTicketType(position, fallbackTicketType),
        ]),
      );
      const eventUnits = unitMap[eventRef]?.items || [];
      const availabilitySummary = buildAvailabilitySummary(
        sortedPositions,
        eventUnits,
        ticketTypeByBatch,
        fallbackTicketType,
      );
      const unitsLoaded = Boolean(unitMap[eventRef]?.items);
      const positionRows = sortedPositions.map((position) => {
        const adjustmentTotal = parsePnlNumber(position.adjustment_total) || 0;
        const positionUnits = eventUnits.filter((unit) => unit.batch_code === position.source_uuid);
        const adjustment = convertEventAdjustment(
          adjustmentTotal, positionUnits, displayCurrency, currencySnapshot,
          EVENT_CURRENCIES[position.twz_location || position.source_tab] || null,
          `Adjustments for ${position.source_uuid || position.event_ref}`,
        );
        return {
          ...position,
          row_id: `position-${position.event_id}`,
          row_type: 'position',
          event_key: eventKey,
          month_label: '',
          month_break: false,
          weekday_label: getWeekdayLabel(position.event_date),
          display_id: `${position.source_uuid || position.event_ref || ''}`,
          availability: availabilitySummary.byBatch.get(position.source_uuid) ?? (unitsLoaded ? 0 : null),
          adjustment_total: adjustmentTotal,
          display_adjustment_total: adjustment.value,
          unit_pnl: unitsLoaded && !adjustment.blocked ? calculateUnitViewPnl(positionUnits, adjustment.value) : null,
          currency_issues: [...new Set([...currencyIssues(positionUnits), ...adjustment.issues])],
          user_login_entries: [{
            userId: position.user_id || null,
            username: position.username || position.assigned_user_raw || 'Unassigned',
            vendor: position.vendor || '',
            platform: position.platform || '',
            batchCode: position.source_uuid || '',
          }],
        };
      });
      const eventUnitPnl = unitsLoaded
        ? (positionRows.some((position) => position.unit_pnl == null) ? null : positionRows.reduce((total, position) => total + position.unit_pnl, 0))
        : null;
      previousMonthKey = monthKey || previousMonthKey;

      return {
        eventKey,
        monthLabel,
        monthBreak,
        positions: sortedPositions,
        availabilityByTicketType: availabilitySummary.byTicketType,
        parent: {
          row_id: `event-${eventKey}`,
          row_type: 'event',
          event_key: eventKey,
          event_ref: eventRef,
          event_id: first?.event_id || null,
          event_date: first?.event_date || null,
          event_name: first?.event_name || '',
          artist: first?.artist || '',
          primary_event_link: first?.primary_event_link || '',
          secondary_artist: distinctSecondaryArtists.length > 1 ? 'Multiple' : (distinctSecondaryArtists[0] || ''),
          ticket_type: distinctTicketTypes.length > 1 ? 'Multiple' : (distinctTicketTypes[0] || ''),
          availability: availabilitySummary.total,
          listing_status: distinctListingStatuses.length > 1 ? 'Multiple' : (distinctListingStatuses[0] || 'NEW'),
          venue: first?.venue || '',
          location: distinctLocations.length > 1 ? 'Multiple' : (distinctLocations[0] || ''),
          twz_location: distinctTwzLocations.length > 1 ? 'Multiple' : (distinctTwzLocations[0] || ''),
          vendor: distinctVendors.length > 1 ? 'Multiple' : (distinctVendors[0] || ''),
          platform: first?.platform || '',
          sold_out: sortedPositions.every((position) => Boolean(position.sold_out)),
          username: sortedPositions.length === 1
            ? (first?.username || '')
            : `${sortedPositions.filter((position) => position.username).length} pos`,
          user_id: sortedPositions.length === 1 ? (first?.user_id || null) : null,
          user_login_entries: sortedPositions.map((position) => ({
            userId: position.user_id || null,
            username: position.username || position.assigned_user_raw || 'Unassigned',
            vendor: position.vendor || '',
            platform: position.platform || '',
            batchCode: position.source_uuid || '',
          })),
          assigned_user_raw: sortedPositions.length === 1 ? (first?.assigned_user_raw || '') : '',
          source_tab: first?.source_tab || '',
          source_uuid: `${sortedPositions.length} positions`,
          adjustment_total: null,
          unit_pnl: eventUnitPnl,
          currency_issues: [...new Set(positionRows.flatMap((position) => position.currency_issues))],
          month_label: monthLabel,
          month_break: monthBreak,
          weekday_label: getWeekdayLabel(first?.event_date),
          display_id: `${expandedEventIds.has(eventKey) ? 'v' : '>'} ${first?.event_ref || eventKey}`,
        },
        children: positionRows,
      };
    });
  }, [monthFilteredItems, expandedEventIds, unitMap, displayCurrency, currencySnapshot]);

  const displayRows = useMemo(() => groupedEvents.flatMap((group) => {
    const rows = [group.parent];
    if (expandedEventIds.has(group.eventKey)) {
      rows.push(...group.children);
    }
    return rows;
  }), [groupedEvents, expandedEventIds]);

  const listingRows = useMemo(() => {
    const userKey = (value) => String(value || '').trim().toLowerCase();
    const filterTransfersForPosition = (transfers, position) => {
      const rowBatchCode = String(position?.source_uuid || '').trim();
      const rowUserId = String(position?.user_id || '').trim();
      const rowUserKeys = new Set([
        userKey(position?.username),
        userKey(position?.assigned_user_raw),
      ].filter(Boolean));

      return transfers.filter((transfer) => {
        const transferBatchCode = String(transfer?.batch_code || '').trim();
        if (rowBatchCode && transferBatchCode) {
          return rowBatchCode === transferBatchCode;
        }
        const transferUserId = String(transfer?.transfer_user_id || '').trim();
        if (rowUserId && transferUserId && rowUserId === transferUserId) {
          return true;
        }
        return rowUserKeys.has(userKey(transfer?.transfer_username));
      });
    };

    const summarizeTicketType = (group, ticketType) => {
      const eventRef = group.parent.event_ref;
      const normalizedTicketType = normalizeListingTicketType(ticketType);
      const ticketTypeByBatch = new Map(
        group.children.map((child) => [
          child.source_uuid,
          normalizeListingTicketType(child.ticket_type || group.parent.ticket_type),
        ]),
      );
      const eventUnits = unitMap[eventRef]?.items || [];
      const units = eventUnits.filter((unit) => (
        getUnitTicketType(unit, ticketTypeByBatch, group.parent.ticket_type || 'Unknown') === normalizedTicketType
      ));
      const fallbackPositions = group.children.filter((child) => (
        normalizeListingTicketType(child.ticket_type || group.parent.ticket_type) === normalizedTicketType
      ));
      const fallbackAvailability = fallbackPositions.reduce(
        (total, position) => total + (parseAvailabilityCount(position.availability) || 0),
        0,
      );
      const availability = units.length
        ? units.filter(isAvailableUnit).length
        : fallbackAvailability;
      const totalInventory = units.length || availability;
      const soldCount = units.filter((unit) => originalSellPrice(unit) != null).length;
      const primary = listingMap[buildListingKey(eventRef, normalizedTicketType, 'primary')] || {
        event_ref: eventRef,
        ticket_type: normalizedTicketType,
        listing_slot: 'primary',
        sale_point: 'Skybox',
        quantity: 0,
        shown: null,
        operator: '',
        monitor: '',
      };
      const secondary = listingMap[buildListingKey(eventRef, normalizedTicketType, 'secondary')] || {
        event_ref: eventRef,
        ticket_type: normalizedTicketType,
        listing_slot: 'secondary',
        sale_point: '',
        quantity: 0,
      };
      const transfers = transferMap[buildTransferKey(eventRef, normalizedTicketType)] || [];
      const transferred = summarizeTransferQuantity(transfers);
      const listedQuantity = getListingNumber(primary, 'quantity') + getListingNumber(secondary, 'quantity');
      let listedRemaining = listedQuantity;
      const positionDetails = fallbackPositions.map((position, index) => {
        const batchUnits = units.filter((unit) => unit.batch_code === position.source_uuid);
        const positionAvailability = batchUnits.length
          ? batchUnits.filter(isAvailableUnit).length
          : (parseAvailabilityCount(position.availability) || 0);
        const positionInventory = batchUnits.length || positionAvailability;
        const positionSoldCount = batchUnits.filter((unit) => originalSellPrice(unit) != null).length;
        const positionTransfers = filterTransfersForPosition(transfers, position);
        const positionTransferred = summarizeTransferQuantity(positionTransfers);
        const isLastPosition = index === fallbackPositions.length - 1;
        const unitsToList = isLastPosition
          ? positionAvailability - listedRemaining
          : positionAvailability - Math.min(positionAvailability, listedRemaining);

        listedRemaining = Math.max(0, listedRemaining - positionAvailability);

        return {
          eventId: position.event_id,
          batchCode: position.source_uuid,
          avgBuy: averageUnitBuy(batchUnits),
          buyBlocked: hasBlockedCurrency(batchUnits, 'buy'),
          unitsToList,
          outstanding: Math.max(positionSoldCount - positionTransferred, 0),
          totalLeft: positionInventory - positionTransferred,
          transfers: positionTransfers,
          transferUsers: (position.user_id || position.username)
            ? [{
              user_id: position.user_id,
              username: position.username || position.assigned_user_raw || '',
            }]
            : [],
        };
      });
      const positionDetailsByEventId = new Map(
        positionDetails.map((detail) => [String(detail.eventId || ''), detail]),
      );
      const positionDetailsByBatch = new Map(
        positionDetails.map((detail) => [String(detail.batchCode || ''), detail]),
      );

      return {
        ticketType: normalizedTicketType,
        avgBuy: averageUnitBuy(units),
        buyBlocked: hasBlockedCurrency(units, 'buy'),
        unitsLeft: availability - listedQuantity,
        primary,
        secondary,
        transfers,
        transferred,
        outstanding: Math.max(soldCount - transferred, 0),
        totalLeft: totalInventory - transferred,
        positionDetailsByEventId,
        positionDetailsByBatch,
        transferUsers: fallbackPositions
          .filter((position) => position.user_id || position.username)
          .map((position) => ({
            user_id: position.user_id,
            username: position.username || position.assigned_user_raw || '',
          })),
      };
    };

    const enrichListingRow = (group, row, summaries) => {
      const isParent = row.row_type === 'event';
      const rowTicketType = normalizeListingTicketType(row.ticket_type || group.parent.ticket_type);
      const usableSummaries = isParent && rowTicketType === 'Multiple'
        ? summaries
        : summaries.filter((summary) => summary.ticketType === rowTicketType);
      const activeSummaries = usableSummaries.length ? usableSummaries : summaries;
      const firstSummary = activeSummaries[0];
      const operatorForSummary = (summary) => (
        summary.transfers.find((transfer) => transfer?.operator)?.operator
        || summary.primary.operator
        || ''
      );
      const distinctOperators = Array.from(new Set(
        activeSummaries.map(operatorForSummary).filter(Boolean),
      ));
      const distinctMonitors = Array.from(new Set(
        activeSummaries.map((summary) => summary.primary.monitor).filter(Boolean),
      ));
      const canEditListing = Boolean(firstSummary) && !(isParent && activeSummaries.length > 1);
      const avgBuyValues = activeSummaries
        .map((summary) => summary.avgBuy)
        .filter((value) => value != null);
      const positionDetails = isParent
        ? []
        : activeSummaries
          .map((summary) => (
            summary.positionDetailsByEventId.get(String(row.event_id || ''))
              || summary.positionDetailsByBatch.get(String(row.source_uuid || ''))
          ))
          .filter(Boolean);
      const activePositionDetails = positionDetails.length ? positionDetails : null;
      const positionTransferOperator = activePositionDetails
        ? activePositionDetails
          .flatMap((detail) => detail.transfers)
          .find((transfer) => transfer?.operator)?.operator
        : '';
      const positionAvgBuyValues = (activePositionDetails || [])
        .map((detail) => detail.avgBuy)
        .filter((value) => value != null);

      return {
        ...row,
        listing_status: row.listing_status || 'NEW',
        listing_ticket_type: firstSummary?.ticketType || rowTicketType,
        listing_can_edit: canEditListing,
        listing_avg_buy: (activePositionDetails || activeSummaries).some((summary) => summary.buyBlocked) ? null : activePositionDetails
          ? (
            positionAvgBuyValues.length
              ? positionAvgBuyValues.reduce((total, value) => total + value, 0) / positionAvgBuyValues.length
              : null
          )
          : (
            avgBuyValues.length
              ? avgBuyValues.reduce((total, value) => total + value, 0) / avgBuyValues.length
              : null
          ),
        listing_units_left: activePositionDetails
          ? activePositionDetails.reduce((total, detail) => total + detail.unitsToList, 0)
          : activeSummaries.reduce((total, summary) => total + summary.unitsLeft, 0),
        listing_operator: positionTransferOperator || (
          activeSummaries.length > 1
            ? (distinctOperators.length === 1 ? distinctOperators[0] : '')
            : operatorForSummary(firstSummary)
        ),
        listing_monitor: activeSummaries.length > 1
          ? (distinctMonitors.length === 1 ? distinctMonitors[0] : '')
          : (firstSummary?.primary.monitor || ''),
        listing_primary_sale_point: 'Skybox',
        listing_primary_quantity: activeSummaries.length === 1 ? getListingNumber(firstSummary?.primary, 'quantity') : null,
        listing_primary_shown: activeSummaries.length === 1 ? (firstSummary?.primary.shown ?? null) : null,
        listing_secondary_sale_point: activeSummaries.length === 1 ? (firstSummary?.secondary.sale_point || '') : '',
        listing_secondary_quantity: activeSummaries.length === 1 ? getListingNumber(firstSummary?.secondary, 'quantity') : null,
        listing_outstanding: activePositionDetails
          ? activePositionDetails.reduce((total, detail) => total + detail.outstanding, 0)
          : activeSummaries.reduce((total, summary) => total + summary.outstanding, 0),
        listing_total_left: activePositionDetails
          ? activePositionDetails.reduce((total, detail) => total + detail.totalLeft, 0)
          : activeSummaries.reduce((total, summary) => total + summary.totalLeft, 0),
        listing_transfers: activePositionDetails
          ? activePositionDetails.flatMap((detail) => detail.transfers)
          : activeSummaries.flatMap((summary) => summary.transfers),
        listing_transfer_users: activePositionDetails
          ? activePositionDetails.flatMap((detail) => detail.transferUsers)
          : activeSummaries.flatMap((summary) => summary.transferUsers),
      };
    };

    return groupedEvents.flatMap((group) => {
      const ticketTypes = Array.from(new Set(
        group.children
          .map((child) => normalizeListingTicketType(child.ticket_type || group.parent.ticket_type))
          .filter(Boolean),
      ));
      const summaries = (ticketTypes.length ? ticketTypes : [normalizeListingTicketType(group.parent.ticket_type)])
        .map((ticketType) => summarizeTicketType(group, ticketType));
      const rows = [enrichListingRow(group, group.parent, summaries)];
      if (expandedEventIds.has(group.eventKey)) {
        rows.push(...group.children.map((child) => enrichListingRow(group, child, summaries)));
      }
      return rows;
    });
  }, [expandedEventIds, groupedEvents, listingMap, transferMap, unitMap]);

  const originalCurrencySummary = useMemo(() => {
    const units = groupedEvents.flatMap((group) => {
      const batches = new Map(group.children.map((child) => [child.source_uuid, child.ticket_type]));
      const locations = new Map(group.children.map((child) => [child.source_uuid, child.twz_location || child.source_tab]));
      return (rawUnitMap[group.parent.event_ref]?.items || []).filter((unit) => (
        batches.has(unit.batch_code)
        && String(unit.type_label || batches.get(unit.batch_code) || group.parent.ticket_type || '').trim().toUpperCase() !== 'VOID'
      )).map((unit) => ({ ...unit, twz_location: locations.get(unit.batch_code) || group.parent.twz_location || group.parent.source_tab }));
    });
    const adjustments = groupedEvents.flatMap((group) => group.children
      .filter((position) => (
        parsePnlNumber(position.adjustment_total)
        && getPositionTicketType(position, group.parent.ticket_type || 'Unknown').toUpperCase() !== 'VOID'
      ))
      .map((position) => {
        const positionUnits = (rawUnitMap[group.parent.event_ref]?.items || [])
          .filter((unit) => unit.batch_code === position.source_uuid);
        const location = position.twz_location || position.source_tab || group.parent.twz_location || group.parent.source_tab;
        const { currency, assumed } = adjustmentOriginalCurrency(positionUnits, EVENT_CURRENCIES[location]);
        return {
          amount: parsePnlNumber(position.adjustment_total),
          currency,
          assumed,
          label: `adjustments on ${position.source_uuid || position.event_ref}`,
        };
      }));
    return summarizeOriginalCurrencies(units, adjustments);
  }, [groupedEvents, rawUnitMap]);

  const pnlRows = useMemo(() => {
    const monthBlocks = [];
    const monthBlockByKey = new Map();

    groupedEvents.forEach((group) => {
      const eventRef = group.parent.event_ref;
      const monthKey = group.parent.event_date ? group.parent.event_date.slice(0, 7) : 'unknown';
      const monthLabel = group.monthLabel || group.parent.month_label || 'No Date';

      if (!monthBlockByKey.has(monthKey)) {
        const block = {
          key: monthKey,
          monthLabel,
          summary: createPnlAccumulator(),
          availability: 0,
          rows: [],
        };
        monthBlockByKey.set(monthKey, block);
        monthBlocks.push(block);
      }

      const monthBlock = monthBlockByKey.get(monthKey);
      const eventUnits = unitMap[eventRef]?.items || [];
      const ticketTypeByBatch = new Map(
        group.children.map((child) => [
          child.source_uuid,
          child.ticket_type || group.parent.ticket_type || 'Unknown',
        ]),
      );
      const summariesByTicketType = new Map();

      eventUnits.forEach((unit) => {
        const ticketType = String(
          unit.type_label
            || ticketTypeByBatch.get(unit.batch_code)
            || group.parent.ticket_type
            || 'Unknown',
        ).trim() || 'Unknown';
        if (ticketType.toUpperCase() === 'VOID') {
          return;
        }

        if (!summariesByTicketType.has(ticketType)) {
          summariesByTicketType.set(ticketType, createPnlAccumulator());
        }
        addUnitToPnl(summariesByTicketType.get(ticketType), unit);
      });

      const adjustmentIssuesByTicketType = new Map();
      group.children.forEach((position) => {
        const adjustmentTotal = parsePnlNumber(position.adjustment_total) || 0;
        if (!adjustmentTotal) return;
        const ticketType = getPositionTicketType(position, group.parent.ticket_type || 'Unknown');
        if (ticketType.toUpperCase() === 'VOID') return;
        const adjustment = convertEventAdjustment(
          adjustmentTotal,
          eventUnits.filter((unit) => unit.batch_code === position.source_uuid),
          displayCurrency,
          currencySnapshot,
          EVENT_CURRENCIES[position.twz_location || position.source_tab] || null,
          `Adjustments for ${position.source_uuid || position.event_ref}`,
        );
        if (!summariesByTicketType.has(ticketType)) {
          summariesByTicketType.set(ticketType, createPnlAccumulator());
        }
        const summary = summariesByTicketType.get(ticketType);
        if (adjustment.blocked) {
          summary.adjustmentBlocked = true;
        } else {
          summary.adjustmentTotal += parsePnlNumber(adjustment.value) || 0;
        }
        if (adjustment.issues.length) {
          adjustmentIssuesByTicketType.set(ticketType, [
            ...(adjustmentIssuesByTicketType.get(ticketType) || []), ...adjustment.issues,
          ]);
        }
      });

      Array.from(summariesByTicketType.entries()).forEach(([ticketType, summary], ticketTypeIndex) => {
        const rowAvailability = group.availabilityByTicketType?.get(ticketType) || 0;
        const userBreakdown = buildPnlUserBreakdown(
          group.children,
          eventUnits,
          ticketType,
          ticketTypeByBatch,
          group.parent.ticket_type || 'Unknown',
        );
        const userValue = userBreakdown.length === 1
          ? userBreakdown[0].username
          : (userBreakdown.length > 1 ? `${userBreakdown.length} positions` : '');
        mergePnlSummary(monthBlock.summary, summary);
        monthBlock.availability += rowAvailability;
        monthBlock.rows.push({
          ...group.parent,
          ...finalizePnlSummary(summary),
          row_id: `pnl-${group.eventKey}-${normalizePnlKey(ticketType)}-${ticketTypeIndex}`,
          row_type: 'pnl',
          pnl_month_key: monthBlock.key,
          month_label: '',
          month_break: false,
          display_id: String(group.parent.event_ref || group.eventKey || ''),
          ticket_type: ticketType,
          availability: rowAvailability,
          username: userValue,
          pnl_user_positions: userBreakdown,
          currency_issues: [...new Set([
            ...currencyIssues(eventUnits.filter((unit) => (
              getUnitTicketType(unit, ticketTypeByBatch, group.parent.ticket_type || 'Unknown') === ticketType
            ))),
            ...(adjustmentIssuesByTicketType.get(ticketType) || []),
          ])],
          assigned_user_raw: '',
        });
      });
    });

    return monthBlocks.flatMap((block) => {
      const monthSummary = finalizePnlSummary(block.summary);
      const isCollapsed = collapsedPnlMonths.has(block.key);
      const summaryRow = {
        row_id: `pnl-month-${block.key}`,
        row_type: 'pnl_month_summary',
        pnl_month_key: block.key,
        pnl_month_collapsed: isCollapsed,
        month_label: block.monthLabel,
        month_break: true,
        display_id: 'Summary',
        weekday_label: '',
        event_date: null,
        artist: '',
        event_name: '',
        secondary_artist: '',
        ticket_type: '',
        venue: '',
        location: '',
        vendor: '',
        platform: '',
        sold_out: null,
        username: '',
        pnl_user_positions: [],
        assigned_user_raw: '',
        source_tab: '',
        source_uuid: '',
        ...monthSummary,
        currency_issues: [...new Set(block.rows.flatMap((row) => row.currency_issues || []))],
        availability: block.availability,
      };

      return [
        summaryRow,
        ...(isCollapsed ? [] : block.rows),
      ];
    });
  }, [collapsedPnlMonths, currencySnapshot, displayCurrency, groupedEvents, unitMap]);

  const gridRows = useMemo(
    () => {
      if (activeView === 'pnl') return pnlRows;
      if (activeView === 'listings') return listingRows;
      return displayRows;
    },
    [activeView, displayRows, listingRows, pnlRows],
  );

  const selectedMoveOrder = useMemo(() => (
    moveOrderPanel?.orderOptions?.find((item) => item.orderId === moveOrderForm.orderId) || null
  ), [moveOrderForm.orderId, moveOrderPanel]);

  const moveToVoidSelected = moveOrderForm.targetUserId === MOVE_TO_VOID_TARGET;
  const moveQuantityNeeded = selectedMoveOrder
    ? selectedMoveOrder.quantity - ((selectedMoveOrder.holders || []).find((holder) => (
      String(holder.userId || '') === String(moveOrderForm.targetUserId)
    ))?.quantity || 0)
    : 0;

  const moveTargetUserOptions = useMemo(() => {
    const positions = (
      groupedEvents.find((item) => item.parent.event_ref === moveOrderPanel?.rowData?.event_ref)?.children || []
    );
    const holders = selectedMoveOrder?.holders || [];
    const wholeOrderUserId = holders.length
      ? (holders.length === 1 ? String(holders[0].userId || '') : '')
      : String(moveOrderPanel?.rowData?.user_id || '');
    const userOptions = Array.from(
      new Map(
        positions
          .filter((position) => (
            position.user_id
            && String(position.user_id) !== wholeOrderUserId
          ))
          .map((position) => [
            String(position.user_id),
            {
              user_id: position.user_id,
              username: position.username || position.assigned_user_raw || 'Unknown user',
            },
          ]),
      ).values(),
    ).sort((left, right) => String(left.username || '').localeCompare(String(right.username || '')));
    const hasOtherVoidPosition = positions.some((position) => (
      normalizeListingTicketType(position.ticket_type).toUpperCase() === 'VOID'
      && String(position.source_uuid || '') !== String(moveOrderPanel?.rowData?.source_uuid || '')
    ));
    return hasOtherVoidPosition
      ? [...userOptions, { user_id: MOVE_TO_VOID_TARGET, username: 'VOID / To Buy' }]
      : userOptions;
  }, [groupedEvents, moveOrderPanel, selectedMoveOrder]);

  const moveTargetAvailability = useMemo(() => {
    if (!moveOrderPanel?.rowData?.event_ref || !moveOrderForm.targetUserId || !selectedMoveOrder) {
      return { available: 0, eligible: 0, hasPosition: false };
    }

    const eventRef = moveOrderPanel.rowData.event_ref;
    const normalizedTicketType = normalizeListingTicketType(selectedMoveOrder.ticketType);
    const group = groupedEvents.find((item) => item.parent.event_ref === eventRef);
    const targetBatchCodes = new Set(
      (group?.children || [])
        .filter((position) => (
          moveToVoidSelected
            ? normalizeListingTicketType(position.ticket_type).toUpperCase() === 'VOID'
            : (
              String(position.user_id || '') === String(moveOrderForm.targetUserId)
              && normalizeListingTicketType(position.ticket_type || group?.parent?.ticket_type) === normalizedTicketType
            )
        ))
        .map((position) => position.source_uuid)
        .filter(Boolean),
    );

    const targetUnits = (unitMap[eventRef]?.items || []).filter((unit) => targetBatchCodes.has(unit.batch_code));
    return {
      available: targetUnits.filter((unit) => (
        isAvailableUnit(unit)
        && !unit.tikwiz_order_id
        && unit.sell_unit_price == null
      )).length,
      eligible: targetUnits.filter((unit) => !unit.tikwiz_order_id && unit.sell_unit_price == null).length,
      hasPosition: targetBatchCodes.size > 0,
    };
  }, [groupedEvents, moveOrderForm.targetUserId, moveOrderPanel, moveToVoidSelected, selectedMoveOrder, unitMap]);

  const allPositionsExpanded = useMemo(
    () => groupedEvents.length > 0 && groupedEvents.every((group) => expandedEventIds.has(group.eventKey)),
    [groupedEvents, expandedEventIds],
  );

  const handleToggleAllPositions = () => {
    if (allPositionsExpanded) {
      setExpandedEventIds(new Set());
      return;
    }
    setExpandedEventIds(new Set(groupedEvents.map((group) => group.eventKey)));
  };

  const unitSideColumnCount = useMemo(
    () => displayRows.reduce((maxCount, row) => {
      if (row.row_type !== 'position') {
        return maxCount;
      }
      return Math.max(maxCount, getBatchUnits(unitMap, row).length, getSellColumnUnits(unitMap, row).length);
    }, 0),
    [displayRows, unitMap],
  );

  const buyUnitColumnDefs = useMemo(
    () => Array.from({ length: unitSideColumnCount }, (_, index) => {
      const ticketIndex = index + 1;
      return {
        headerName: `Buy ${ticketIndex}`,
        field: `buy_ticket_${ticketIndex}`,
        minWidth: 120,
        width: 120,
        editable: (params) => activeView === 'unit' && params.data?.row_type === 'position',
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellClass: 'events-ticket-cell',
        cellRenderer: (params) => {
          const unit = getBatchUnits(unitMap, params.data)[index];
          return renderUnitPriceCell(displayBuy(unit), unit?.unit_code);
        },
        valueGetter: (params) => {
          const unit = getBatchUnits(unitMap, params.data)[index];
          return formatUnitPrice(unit?.unit_price);
        },
        tooltipValueGetter: (params) => {
          const unit = getBatchUnits(unitMap, params.data)[index];
          if (!unit) return '';
          return `${unit.unit_code} | ${unit.status}${unit.tikwiz_order_id ? ` | Order ${unit.tikwiz_order_id}` : ''} | ${currencyPriceTooltip(unit, 'buy')}`;
        },
      };
    }),
    [activeView, unitSideColumnCount, unitMap],
  );

  const sellUnitColumnDefs = useMemo(
    () => Array.from({ length: unitSideColumnCount }, (_, index) => {
      const ticketIndex = index + 1;
      return {
        headerName: `Sell ${ticketIndex}`,
        field: `sell_ticket_${ticketIndex}`,
        minWidth: 120,
        width: 120,
        editable: (params) => activeView === 'unit' && params.data?.row_type === 'position',
        sortable: false,
        filter: false,
        floatingFilter: false,
        headerClass: index === 0 ? 'events-buy-sell-divider' : '',
        cellClass: index === 0 ? 'events-ticket-cell events-sell-ticket-cell events-buy-sell-divider' : 'events-ticket-cell events-sell-ticket-cell',
        cellRenderer: (params) => {
          const unit = getSellColumnUnits(unitMap, params.data)[index];
          if (displaySell(unit) == null) return '';
          return renderUnitPriceCell(
            displaySell(unit),
            unit?.tikwiz_order_id || unit?.unit_code,
            'events-sell-ticket-card',
          );
        },
        valueGetter: (params) => {
          const unit = getSellColumnUnits(unitMap, params.data)[index];
          return formatUnitPrice(unit?.sell_unit_price);
        },
        tooltipValueGetter: (params) => {
          const unit = getSellColumnUnits(unitMap, params.data)[index];
          if (!unit) return '';
          return `${unit.unit_code} | ${unit.status}${unit.tikwiz_order_id ? ` | Order ${unit.tikwiz_order_id}` : ''}${unit.order_status ? ` | ${unit.order_status}` : ''} | ${currencyPriceTooltip(unit, 'sell')}`;
        },
      };
    }),
    [activeView, unitSideColumnCount, unitMap],
  );

  const unitPnlColumn = useMemo(() => ({
    headerName: 'PNL',
    field: 'unit_pnl',
    headerClass: 'events-centered-header',
    minWidth: 110,
    width: 110,
    editable: false,
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => formatPnlMoney(params.value),
    cellClass: (params) => getPnlToneClass(params.value),
  }), []);

  const unitBaseColumnDefs = useMemo(() => {
    if (!canSeePnl) {
      return baseColumnDefs;
    }

    return baseColumnDefs.flatMap((column) => (
      column.field === 'availability' ? [column, unitPnlColumn] : [column]
    ));
  }, [baseColumnDefs, canSeePnl, unitPnlColumn]);

  const adjustmentColumnDef = useMemo(() => ({
    headerName: 'Adjustments',
    field: 'adjustment_total',
    valueGetter: ({ data }) => data?.row_type === 'position' ? data.display_adjustment_total : null,
    minWidth: 120,
    width: 120,
    editable: false,
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) => formatPnlMoney(params.value),
    cellClass: (params) => (
      params.data?.row_type === 'position'
        ? `events-adjustment-cell ${getPnlToneClass(params.value)}`
        : getPnlToneClass(params.value)
    ),
    tooltipValueGetter: (params) => (
      params.data?.row_type === 'position' ? 'Double-click to edit adjustments in the original purchase currency' : ''
    ),
  }), []);

  const twzLocationColumnDef = useMemo(() => ({
    headerName: 'TWZ Location',
    field: 'twz_location',
    minWidth: 140,
    width: 140,
    editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
    singleClickEdit: true,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: EVENT_TWZ_LOCATION_OPTIONS,
    },
    filter: 'agTextColumnFilter',
  }), []);

  const pnlColumnDefs = useMemo(() => [
    {
      headerName: 'Month',
      field: 'month_label',
      pinned: 'left',
      minWidth: 88,
      maxWidth: 88,
      sortable: false,
      filter: false,
      floatingFilter: false,
      editable: false,
      cellClass: 'events-month-cell',
      cellRenderer: (params) => {
        if (params.data?.row_type !== 'pnl_month_summary') return '';
        return React.createElement(
          'span',
          { className: 'events-pnl-month-toggle' },
          React.createElement(
            'span',
            { className: 'events-pnl-month-arrow' },
            params.data?.pnl_month_collapsed ? '>' : 'v',
          ),
          React.createElement('span', null, params.value),
        );
      },
    },
    {
      headerName: 'ID',
      field: 'display_id',
      pinned: 'left',
      minWidth: 110,
      maxWidth: 110,
      sortable: false,
      filter: false,
      floatingFilter: false,
      editable: false,
      cellClass: (params) => (
        params.data?.row_type === 'pnl_month_summary'
          ? 'events-pnl-summary-cell'
          : 'events-id-cell'
      ),
    },
    {
      headerName: 'Profit/Loss',
      field: 'pnl_profit_loss',
      pinned: 'left',
      minWidth: 120,
      width: 120,
      editable: false,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => (
        params.data?.row_type === 'pnl_month_summary' && displayCurrency
          ? `${displayCurrency} ${formatPnlMoney(params.value)}`.trim()
          : formatPnlMoney(params.value)
      ),
      cellClass: (params) => getPnlToneClass(params.value),
    },
    {
      headerName: 'ROI %',
      field: 'pnl_roi',
      pinned: 'left',
      minWidth: 100,
      width: 100,
      editable: false,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => formatPnlPercent(params.value),
      cellClass: (params) => getPnlToneClass(params.value),
    },
    {
      headerName: 'Day',
      field: 'weekday_label',
      minWidth: 88,
      maxWidth: 88,
      sortable: false,
      filter: false,
      floatingFilter: false,
      editable: false,
      cellClass: 'events-dow-cell',
      cellRenderer: (params) => {
        if (!params.value) return '';
        return React.createElement(
          'span',
          { className: getWeekdayClassName(params.data?.event_date) },
          params.value,
        );
      },
    },
    {
      headerName: 'Date',
      field: 'event_date',
      minWidth: 100,
      maxWidth: 100,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => formatEventDate(params.value),
    },
    {
      headerName: 'Artist',
      field: 'artist',
      minWidth: 135,
      width: 150,
      maxWidth: 190,
      editable: false,
      cellClass: (params) => {
        if (params.data?.row_type === 'pnl_month_summary') return 'events-pnl-summary-cell';
        return !isDashboardRoute
          && params.data?.row_type === 'event'
          && String(params.data?.primary_event_link || '').trim()
          ? 'events-linked-artist-cell'
          : '';
      },
    },
    {
      headerName: 'Ticket Type',
      field: 'ticket_type',
      minWidth: 150,
      width: 160,
      editable: false,
    },
    {
      headerName: 'Availability',
      field: 'availability',
      minWidth: 105,
      maxWidth: 115,
      editable: false,
      filter: 'agNumberColumnFilter',
      cellClass: (params) => getAvailabilityCellClass(params.value),
      valueFormatter: (params) => (params.value == null ? '' : params.value),
    },
    {
      headerName: 'User',
      field: 'username',
      minWidth: USERNAME_COL_WIDTH,
      width: USERNAME_COL_WIDTH,
      maxWidth: USERNAME_COL_WIDTH,
      editable: false,
      suppressFloatingFilterButton: true,
      cellRenderer: (params) => {
        if (params.data?.row_type === 'pnl' && params.data?.pnl_user_positions?.length > 1) {
          return React.createElement('span', { className: 'events-pnl-user-count' }, params.value);
        }
        return params.value || '';
      },
      tooltipComponent: UserLoginTooltip,
      tooltipValueGetter: getUserLoginTooltipValue,
    },
    ...readonlyEventDetailColumnsBeforeSoldOut,
    readonlySoldOutColumn,
    ...pnlEventDetailColumnsAfterSoldOut,
    {
      headerName: 'Units',
      field: 'pnl_units',
      minWidth: 90,
      maxWidth: 90,
      editable: false,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'Sold',
      field: 'pnl_sold_units',
      minWidth: 90,
      maxWidth: 90,
      editable: false,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'Avg Buy',
      field: 'pnl_avg_buy',
      minWidth: 110,
      width: 110,
      editable: false,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => formatPnlMoney(params.value),
    },
    {
      headerName: 'Avg Sell',
      field: 'pnl_avg_sell',
      minWidth: 110,
      width: 110,
      editable: false,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => formatPnlMoney(params.value),
    },
    {
      headerName: 'Adjustments',
      field: 'pnl_adjustments',
      minWidth: 120,
      width: 120,
      editable: false,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => formatPnlMoney(params.value),
      cellClass: (params) => getPnlToneClass(params.value),
    },
  ], [displayCurrency, isDashboardRoute, pnlEventDetailColumnsAfterSoldOut, readonlyEventDetailColumnsBeforeSoldOut, readonlySoldOutColumn]);

  const listingsColumnDefs = useMemo(() => {
    const statusValues = listingOptions.statuses?.length ? listingOptions.statuses : DEFAULT_LISTING_STATUSES;
    const monitorValues = ['', ...(listingOptions.monitors?.length ? listingOptions.monitors : DEFAULT_LISTING_MONITORS)];
    const operatorValues = ['', ...(listingOptions.operators || [])];
    const secondarySalePointValues = [
      '',
      ...(listingOptions.salePoints?.length ? listingOptions.salePoints : DEFAULT_LISTING_SALE_POINTS)
        .filter((salePoint) => salePoint !== 'Skybox'),
    ];
    const isListingEditable = (params) => params.data?.listing_can_edit === true;
    const transferTooltip = {
      tooltipComponent: ListingsTransferTooltip,
      tooltipValueGetter: (params) => params.data?.listing_transfers || [],
      cellClass: 'events-listings-transfer-cell',
    };

    return [
      {
        headerName: 'Month',
        field: 'month_label',
        pinned: 'left',
        minWidth: 70,
        maxWidth: 70,
        sortable: false,
        filter: false,
        floatingFilter: false,
        editable: false,
        cellClass: 'events-month-cell',
        cellRenderer: (params) => (params.data?.month_break ? params.value : ''),
      },
      {
        headerName: 'ID',
        field: 'display_id',
        pinned: 'left',
        minWidth: 120,
        maxWidth: 120,
        sortable: false,
        filter: false,
        floatingFilter: false,
        editable: false,
        cellClass: (params) => (
          params.data?.row_type === 'event'
            ? 'events-id-cell events-parent-id-cell'
            : 'events-id-cell events-position-id-cell'
        ),
      },
      {
        headerName: 'Day',
        field: 'weekday_label',
        pinned: 'left',
        minWidth: 88,
        maxWidth: 88,
        sortable: false,
        filter: false,
        floatingFilter: false,
        editable: false,
        cellClass: 'events-dow-cell',
        cellRenderer: (params) => {
          if (!params.value) return '';
          return React.createElement(
            'span',
            { className: getWeekdayClassName(params.data?.event_date) },
            params.value,
          );
        },
      },
      {
        headerName: 'Date',
        field: 'event_date',
        pinned: 'left',
        minWidth: 100,
        maxWidth: 100,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => formatEventDate(params.value),
      },
      {
        headerName: 'Status',
        field: 'listing_status',
        minWidth: 105,
        width: 112,
        editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
        singleClickEdit: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: statusValues,
        },
        valueFormatter: (params) => formatListingStatus(params.value),
        cellClass: (params) => getListingStatusClass(params.value),
      },
      {
        headerName: 'Artist',
        field: 'artist',
        pinned: 'left',
        minWidth: 135,
        width: 150,
        maxWidth: 190,
        editable: false,
        cellClass: (params) => (
          !isDashboardRoute
          && params.data?.row_type === 'event'
          && String(params.data?.primary_event_link || '').trim()
            ? 'events-linked-artist-cell'
            : ''
        ),
      },
      {
        headerName: 'Ticket Type',
        field: 'ticket_type',
        minWidth: 130,
        width: 140,
        editable: false,
        cellClass: (params) => (
          String(params.value || '').trim().toUpperCase() === 'VOID'
            ? 'events-ticket-type-void-cell'
            : ''
        ),
      },
      {
        headerName: 'Availability',
        field: 'availability',
        minWidth: 105,
        maxWidth: 115,
        editable: false,
        filter: 'agNumberColumnFilter',
        cellClass: (params) => getAvailabilityCellClass(params.value),
        valueFormatter: (params) => (params.value == null ? '' : params.value),
      },
      {
        headerName: 'User',
        field: 'username',
        minWidth: USERNAME_COL_WIDTH,
        width: USERNAME_COL_WIDTH,
        maxWidth: USERNAME_COL_WIDTH,
        editable: false,
        suppressFloatingFilterButton: true,
        tooltipComponent: UserLoginTooltip,
        tooltipValueGetter: getUserLoginTooltipValue,
      },
      ...eventDetailColumnsBeforeSoldOut,
      soldOutColumn,
      ...eventDetailColumnsAfterSoldOut,
      {
        headerName: 'Avg Buy',
        field: 'listing_avg_buy',
        minWidth: 95,
        width: 100,
        editable: false,
        filter: 'agNumberColumnFilter',
        valueFormatter: (params) => formatPnlMoney(params.value),
      },
      {
        headerName: 'Units to List',
        field: 'listing_units_left',
        minWidth: 112,
        width: 120,
        editable: false,
        filter: 'agNumberColumnFilter',
        cellClass: (params) => getListingUnitsLeftClass(params.value),
      },
      {
        headerName: 'Operator',
        field: 'listing_operator',
        minWidth: 105,
        width: 115,
        editable: isListingEditable,
        singleClickEdit: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: operatorValues,
        },
        valueFormatter: (params) => formatListingOperator(params.value),
        cellClass: (params) => getListingOperatorClass(params.value),
      },
      {
        headerName: 'Monitor',
        field: 'listing_monitor',
        minWidth: 105,
        width: 115,
        editable: isListingEditable,
        singleClickEdit: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: monitorValues,
        },
      },
      {
        headerName: 'Skybox',
        field: 'listing_primary_quantity',
        minWidth: 105,
        width: 110,
        editable: isListingEditable,
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Shown',
        field: 'listing_primary_shown',
        minWidth: 85,
        width: 90,
        editable: isListingEditable,
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Other Point',
        field: 'listing_secondary_sale_point',
        minWidth: 110,
        width: 120,
        editable: isListingEditable,
        singleClickEdit: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: secondarySalePointValues,
        },
      },
      {
        headerName: 'Other Qty',
        field: 'listing_secondary_quantity',
        minWidth: 95,
        width: 100,
        editable: isListingEditable,
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Outstanding',
        field: 'listing_outstanding',
        minWidth: 115,
        width: 120,
        editable: false,
        filter: 'agNumberColumnFilter',
        ...transferTooltip,
      },
      {
        headerName: 'Total Left',
        field: 'listing_total_left',
        minWidth: 105,
        width: 110,
        editable: false,
        filter: 'agNumberColumnFilter',
        ...transferTooltip,
      },
    ];
  }, [
    eventDetailColumnsAfterSoldOut,
    eventDetailColumnsBeforeSoldOut,
    isDashboardRoute,
    listingOptions,
    soldOutColumn,
  ]);

  const canExport = EXPORT_VIEWS.includes(activeView);

  // Listing figures (Units to List, Avg Buy) per event row, so the Unit view can export Shopify CSV too.
  const listingRowById = useMemo(
    () => new Map(listingRows.map((row) => [row.row_id, row])),
    [listingRows],
  );

  // "CSV": one line per event on the current page, without position-level / layout columns, plus the event URL.
  const handleExportCsv = useCallback(() => {
    setContextMenu(null);
    const api = gridRef.current?.api;
    if (!api) return;
    const columns = api.getAllDisplayedColumns().filter((column) => {
      const field = column.getColDef().field;
      return Boolean(field) && !EVENT_CSV_EXCLUDED_FIELD.test(field);
    });
    const { start, end } = getCurrentPageRange(api);
    const lines = [];
    for (let index = start; index < end; index += 1) {
      const node = api.getDisplayedRowAtIndex(index);
      const row = node?.data;
      if (row?.row_type !== 'event') continue;
      lines.push([
        ...columns.map((column) => {
          if (column.getColDef().field === 'display_id') return row.event_ref || '';
          return api.getCellValue({ rowNode: node, colKey: column, useFormatted: true }) ?? '';
        }),
        row.primary_event_link || '',
      ]);
    }
    if (!lines.length) {
      setError('Nothing to export: no events on this page.');
      return;
    }
    const header = [
      ...columns.map((column) => column.getColDef().headerName || column.getColDef().field),
      'Event URL',
    ];
    downloadCsvFile(`tikwiz-events-${activeView}-${getExportFileDate()}.csv`, [header, ...lines]);
  }, [activeView]);

  // "Full Data CSV": everything the grid shows on the current page, including position rows.
  const handleExportFullCsv = useCallback(() => {
    setContextMenu(null);
    const api = gridRef.current?.api;
    if (!api) return;
    const { start, end } = getCurrentPageRange(api);
    api.exportDataAsCsv({
      fileName: `tikwiz-events-${activeView}-full-${getExportFileDate()}.csv`,
      shouldRowBeSkipped: ({ node }) => node.rowIndex == null || node.rowIndex < start || node.rowIndex >= end,
    });
  }, [activeView]);

  const handleExportShopifyCsv = useCallback(() => {
    setContextMenu(null);
    const api = gridRef.current?.api;
    if (!api) return;
    const { start, end } = getCurrentPageRange(api);
    const rows = [];
    for (let index = start; index < end; index += 1) {
      const pageRow = api.getDisplayedRowAtIndex(index)?.data;
      if (pageRow?.row_type !== 'event') continue;
      const row = { ...pageRow, ...(listingRowById.get(pageRow.row_id) || {}) };
      if (!((parseAvailabilityCount(row.availability) || 0) > 0)) continue;
      rows.push(row);
    }
    if (!rows.length) {
      setError('Nothing to export: no events on this page have tickets available.');
      return;
    }
    downloadCsvFile(
      `tikwiz-shopify-import-${getExportFileDate()}.csv`,
      [SHOPIFY_IMPORT_HEADER, ...rows.map(buildShopifyImportRow)],
    );
    setError('');
    setMessage(`Exported ${rows.length} event${rows.length === 1 ? '' : 's'} as a Shopify import file (drafts). Fill in Price before importing.`);
  }, [listingRowById]);

  const currencyReviewColumnDef = useMemo(() => ({
    headerName: '', field: 'currency_issues', width: 44, minWidth: 44, maxWidth: 44,
    cellClass: 'events-currency-warning-cell',
    sortable: false, filter: false, floatingFilter: false, editable: false,
    cellRenderer: ({ data }) => data?.currency_issues?.length ? (
      <button type="button" className="events-currency-warning" aria-label="Explain currency issues"
        title="Explain currency assumptions, provisional rates, or missing information"
        onClick={(event) => {
          event.stopPropagation();
          setDetailsPanel(null);
          setCurrencyPanel({ title: data.event_name || data.display_id || 'Currency details', issues: data.currency_issues });
        }}>!</button>
    ) : null,
  }), []);

  const columnDefs = useMemo(() => {
    let columns;
    if (activeView === 'unit') {
      columns = [...unitBaseColumnDefs, ...buyUnitColumnDefs, ...sellUnitColumnDefs, adjustmentColumnDef];
    } else if (activeView === 'pnl') {
      columns = pnlColumnDefs;
    } else if (activeView === 'listings') {
      columns = listingsColumnDefs;
    } else {
      return baseColumnDefs;
    }
    if (isDashboardRoute) return columns;
    return [...columns, {
      ...twzLocationColumnDef,
      editable: (params) => canEditTwzLocation && twzLocationColumnDef.editable(params),
    }, currencyReviewColumnDef];
  }, [activeView, unitBaseColumnDefs, buyUnitColumnDefs, sellUnitColumnDefs, adjustmentColumnDef,
    pnlColumnDefs, listingsColumnDefs, baseColumnDefs, twzLocationColumnDef, canEditTwzLocation,
    currencyReviewColumnDef, isDashboardRoute]);

  const visibleColumnDefs = useMemo(() => {
    if (!showSeasonTickets) {
      return columnDefs;
    }

    const hiddenSeasonFields = new Set(['artist', 'ticket_type']);
    const hiddenSeasonHeaders = new Set(['Artist', 'Ticket Type', 'Ticket']);
    const seasonEventColumn = {
      headerName: 'Event',
      field: 'event_name',
      minWidth: 280,
      width: 320,
      flex: 1.6,
      editable: (params) => params.data?.row_type === 'position' || params.data?.row_type === 'event',
      filter: 'agTextColumnFilter',
    };
    let eventColumnInserted = false;

    const nextColumns = [];
    columnDefs.forEach((column) => {
      const field = column.field || '';
      const headerName = column.headerName || '';

      if (field === 'event_name') {
        if (!eventColumnInserted) {
          nextColumns.push(seasonEventColumn);
          eventColumnInserted = true;
        }
        return;
      }

      if (hiddenSeasonFields.has(field) || hiddenSeasonHeaders.has(headerName)) {
        return;
      }

      nextColumns.push(column);
      if (!eventColumnInserted && field === 'event_date') {
        nextColumns.push(seasonEventColumn);
        eventColumnInserted = true;
      }
    });

    if (!eventColumnInserted) {
      nextColumns.push(seasonEventColumn);
    }
    return nextColumns;
  }, [columnDefs, showSeasonTickets]);

  const loadEvents = async (tabValue, qValue, yearValue = selectedYear) => {
    setLoading(true);
    const result = await eventsService.getDisplay({
      tab: tabValue || undefined,
      q: qValue || undefined,
      year: yearValue,
      limit: 2000,
    });
    if (result.success) {
      setItems(result.items || []);
      setTotalCount(result.totalCount || 0);
      setError('');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const loadSeasonTicketEvents = async (seasonPackage) => {
    if (!seasonPackage) {
      return;
    }
    setLoading(true);
    const result = await eventsService.getDisplay({
      tab: seasonPackage.sourceTab,
      artist: seasonPackage.name,
      startDate: seasonPackage.startDate,
      endDate: seasonPackage.endDate,
      limit: 2000,
    });
    if (result.success) {
      setItems(result.items || []);
      setTotalCount(result.totalCount || 0);
      setError('');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const loadUnits = async (eventRefs) => {
    const refsToFetch = Array.from(
      new Set(eventRefs.filter((eventRef) => eventRef && !unitMap[eventRef])),
    );
    if (!refsToFetch.length) {
      setUnitsError('');
      return;
    }

    setUnitsLoading(true);
    setUnitsError('');
    const results = await Promise.all(refsToFetch.map((eventRef) => eventsService.getUnits(eventRef)));

    const nextUnitMap = {};
    const failedRefs = [];
    results.forEach((result, index) => {
      const eventRef = refsToFetch[index];
      if (result.success) {
        nextUnitMap[eventRef] = result;
      } else {
        failedRefs.push(eventRef);
      }
    });

    if (Object.keys(nextUnitMap).length) {
      setUnitMap((current) => ({ ...current, ...nextUnitMap }));
    }

    setUnitsError(
      failedRefs.length
        ? `Failed to load units for: ${failedRefs.join(', ')}`
        : '',
    );
    setUnitsLoading(false);
  };

  const refreshEventUnits = async (eventRef) => {
    if (!eventRef) {
      return null;
    }

    setUnitsLoading(true);
    setUnitsError('');
    const result = await eventsService.getUnits(eventRef);
    if (!result.success) {
      setUnitsError(result.error);
      setUnitsLoading(false);
      return null;
    }

    setUnitMap((current) => ({
      ...current,
      [eventRef]: result,
    }));
    setDetailsPanel((current) => {
      if (!current || current.eventRef !== eventRef) {
        return current;
      }
      return {
        ...current,
        items: (result.items || []).filter((item) => item.batch_code === current.batchCode),
      };
    });
    setUnitsLoading(false);
    return result;
  };

  const replaceUnitItem = (updatedUnit) => {
    if (!updatedUnit?.unit_id) {
      return;
    }

    setUnitMap((current) => {
      const eventUnits = current[updatedUnit.event_ref];
      if (!eventUnits?.items?.length) {
        return current;
      }

      let changed = false;
      const nextItems = eventUnits.items.map((item) => {
        if (item.unit_id !== updatedUnit.unit_id) {
          return item;
        }
        changed = true;
        return updatedUnit;
      });

      if (!changed) {
        return current;
      }

      return {
        ...current,
        [updatedUnit.event_ref]: {
          ...eventUnits,
          items: nextItems,
        },
      };
    });

    setDetailsPanel((current) => {
      if (!current?.items?.length) {
        return current;
      }

      let changed = false;
      const nextItems = current.items.map((item) => {
        if (item.unit_id !== updatedUnit.unit_id) {
          return item;
        }
        changed = true;
        return updatedUnit;
      });

      return changed ? { ...current, items: nextItems } : current;
    });
  };

  const loadListings = async (eventRefs) => {
    const refsToFetch = Array.from(new Set(eventRefs.filter(Boolean)));
    if (!refsToFetch.length) {
      setListingMap({});
      setTransferMap({});
      setListingsError('');
      return;
    }

    setListingsLoading(true);
    setListingsError('');
    const result = await eventsService.getListings(refsToFetch);
    if (!result.success) {
      setListingsError(result.error);
      setListingsLoading(false);
      return;
    }

    const nextListingMap = {};
    (result.items || []).forEach((item) => {
      nextListingMap[buildListingKey(item.event_ref, item.ticket_type, item.listing_slot)] = item;
    });

    const nextTransferMap = {};
    (result.transfers || []).forEach((item) => {
      const key = buildTransferKey(item.event_ref, item.ticket_type);
      nextTransferMap[key] = [...(nextTransferMap[key] || []), item];
    });

    setListingMap(nextListingMap);
    setTransferMap(nextTransferMap);
    setListingsLoading(false);
  };

  const applyListingItem = (item) => {
    setListingMap((current) => ({
      ...current,
      [buildListingKey(item.event_ref, item.ticket_type, item.listing_slot)]: item,
    }));
  };

  const applyTransferItem = (item) => {
    setTransferMap((current) => {
      const key = buildTransferKey(item.event_ref, item.ticket_type);
      return {
        ...current,
        [key]: [item, ...(current[key] || [])],
      };
    });
  };

  const removeTransferItem = (item) => {
    setTransferMap((current) => {
      const next = {};
      Object.entries(current).forEach(([key, transfers]) => {
        next[key] = (transfers || []).filter((transfer) => (
          item.tikwiz_order_id
            ? transfer.tikwiz_order_id !== item.tikwiz_order_id
            : transfer.transfer_id !== item.transfer_id
        ));
      });
      return next;
    });
  };

  const handleCellValueChanged = async (params) => {
    const { data, colDef, oldValue, newValue, node } = params;
    const field = colDef.field;

    if (!field || oldValue === newValue) {
      return;
    }

    const payload = {};
    if (field === 'listing_status') {
      if (data?.row_type !== 'position' && data?.row_type !== 'event') {
        return;
      }

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { listing_status: newValue ?? 'NEW' });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('Listing status updated.');
        return;
      }

      payload.listing_status = newValue ?? 'NEW';
    } else if (
      [
        'listing_operator',
        'listing_monitor',
        'listing_primary_quantity',
        'listing_primary_shown',
        'listing_secondary_sale_point',
        'listing_secondary_quantity',
      ].includes(field)
    ) {
      if (!data?.listing_can_edit || !data?.event_ref || !data?.listing_ticket_type) {
        node.setDataValue(field, oldValue);
        setError('This listing field is only editable when the row maps to one ticket type.');
        return;
      }

      const primaryQuantity = field === 'listing_primary_quantity'
        ? Number(newValue || 0)
        : Number(data.listing_primary_quantity || 0);
      const primaryShownRaw = field === 'listing_primary_shown' ? newValue : data.listing_primary_shown;
      const primaryShown = primaryShownRaw === '' || primaryShownRaw == null ? null : Number(primaryShownRaw);
      const secondaryQuantity = field === 'listing_secondary_quantity'
        ? Number(newValue || 0)
        : Number(data.listing_secondary_quantity || 0);
      const secondarySalePoint = field === 'listing_secondary_sale_point'
        ? String(newValue || '').trim()
        : String(data.listing_secondary_sale_point || '').trim();

      if (Number.isNaN(primaryQuantity) || Number.isNaN(secondaryQuantity)) {
        node.setDataValue(field, oldValue);
        setError('Listing quantity must be a number.');
        return;
      }
      if (primaryShown != null && Number.isNaN(primaryShown)) {
        node.setDataValue(field, oldValue);
        setError('Shown must be a number.');
        return;
      }

      const isSecondaryField = field === 'listing_secondary_sale_point' || field === 'listing_secondary_quantity';
      if (isSecondaryField && !secondarySalePoint) {
        node.setDataValue(field, oldValue);
        setError('Choose an Other Point before setting the secondary listing.');
        return;
      }

      const listingPayload = isSecondaryField
        ? {
          event_ref: data.event_ref,
          ticket_type: data.listing_ticket_type,
          listing_slot: 'secondary',
          sale_point: secondarySalePoint,
          quantity: secondaryQuantity,
          shown: null,
          operator: null,
          monitor: null,
        }
        : {
          event_ref: data.event_ref,
          ticket_type: data.listing_ticket_type,
          listing_slot: 'primary',
          sale_point: 'Skybox',
          quantity: primaryQuantity,
          shown: primaryShown,
          operator: field === 'listing_operator' ? (newValue || null) : (data.listing_operator || null),
          monitor: field === 'listing_monitor' ? (newValue || null) : (data.listing_monitor || null),
        };

      setError('');
      setMessage('');
      const result = await eventsService.upsertListing(listingPayload);
      if (!result.success) {
        node.setDataValue(field, oldValue);
        setError(result.error);
        return;
      }

      applyListingItem(result.item);
      setMessage('Listing updated.');
      return;
    } else if (field === 'artist') {
      if (data?.row_type !== 'position') {
        return;
      }
      payload.artist = newValue ?? '';
    } else if (field === 'ticket_type') {
      if (data?.row_type !== 'position' && data?.row_type !== 'event') {
        return;
      }

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { ticket_type: newValue ?? '' });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('Ticket type updated.');
        return;
      }

      payload.ticket_type = newValue ?? '';
    } else if (field === 'twz_location') {
      if (!canEditTwzLocation || (data?.row_type !== 'position' && data?.row_type !== 'event')) {
        node.setDataValue(field, oldValue);
        setError('TWZ Location editing is restricted to TS.');
        return;
      }

      const nextLocationRaw = String(newValue ?? '').trim();
      const nextLocation = EVENT_TWZ_LOCATION_OPTIONS.find(
        (option) => option.toLowerCase() === nextLocationRaw.toLowerCase(),
      );
      if (!nextLocation) {
        node.setDataValue(field, oldValue);
        setError(`TWZ Location must be one of: ${EVENT_TWZ_LOCATION_OPTIONS.join(', ')}`);
        return;
      }

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { twz_location: nextLocation });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('TWZ Location updated.');
        return;
      }

      payload.twz_location = nextLocation;
    } else if (field === 'venue') {
      if (data?.row_type !== 'position' && data?.row_type !== 'event') {
        return;
      }

      const nextVenueRaw = String(newValue ?? '').trim();
      const nextVenue = nextVenueRaw ? findVenueOption(venueOptions, nextVenueRaw) : null;
      if (nextVenueRaw && !nextVenue) {
        node.setDataValue(field, oldValue);
        setError('Venue must be selected from the configured venue list. Use Add Venue if it does not exist yet.');
        return;
      }
      const nextVenueName = nextVenue?.name || '';

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { venue: nextVenueName });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('Venue updated.');
        return;
      }

      payload.venue = nextVenueName;
    } else if (field === 'vendor') {
      if (data?.row_type !== 'position' && data?.row_type !== 'event') {
        return;
      }

      const nextVendorRaw = String(newValue ?? '').trim();
      const nextVendor = nextVendorRaw ? findVendorLabel(vendorOptions, nextVendorRaw) : '';
      if (nextVendorRaw && !nextVendor) {
        node.setDataValue(field, oldValue);
        setError(`Vendor must be one of: ${selectVendorLabels(vendorOptions).join(', ')}`);
        return;
      }

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { vendor: nextVendor });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('Vendor updated.');
        return;
      }

      payload.vendor = nextVendor;
    } else if (field === 'platform') {
      if (data?.row_type !== 'position' && data?.row_type !== 'event') {
        return;
      }

      const nextPlatformRaw = String(newValue ?? '').trim();
      const nextPlatform = nextPlatformRaw ? findVendorLabel(platformOptions, nextPlatformRaw) : '';
      if (nextPlatformRaw && !nextPlatform) {
        node.setDataValue(field, oldValue);
        setError(`Platform must be one of: ${selectVendorLabels(platformOptions).join(', ')}`);
        return;
      }

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { platform: nextPlatform });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('Platform updated.');
        return;
      }

      payload.platform = nextPlatform;
    } else if (field === 'event_name') {
      if (data?.row_type !== 'position' && data?.row_type !== 'event') {
        return;
      }

      const nextEventName = String(newValue ?? '').trim();
      if (!nextEventName) {
        node.setDataValue(field, oldValue);
        setError('Event Name cannot be blank.');
        return;
      }

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { event_name: nextEventName });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('Event Name updated.');
        return;
      }

      payload.event_name = nextEventName;
    } else if (field === 'sold_out') {
      if (data?.row_type !== 'position' && data?.row_type !== 'event') {
        return;
      }

      const nextSoldOut = newValue === true
        || String(newValue || '').toLowerCase() === 'yes'
        || String(newValue || '').toLowerCase() === 'true';

      if (data?.row_type === 'event') {
        const targetItems = items.filter((item) => getEventGroupKey(item) === data.event_key);
        if (!targetItems.length) {
          node.setDataValue(field, oldValue);
          setError('No event positions found for this row.');
          return;
        }

        setError('');
        setMessage('');
        const updatedItems = [];
        for (const item of targetItems) {
          const result = await eventsService.updateEvent(item.event_id, { sold_out: nextSoldOut });
          if (!result.success) {
            node.setDataValue(field, oldValue);
            setError(result.error);
            return;
          }
          updatedItems.push(result.item);
        }

        setItems((currentItems) => currentItems.map((item) => {
          const updated = updatedItems.find((nextItem) => nextItem.event_id === item.event_id);
          return updated || item;
        }));
        setMessage('Sold Out updated.');
        return;
      }

      payload.sold_out = nextSoldOut;
    } else if (field.startsWith('buy_ticket_')) {
      if (data?.row_type !== 'position') {
        return;
      }

      const ticketIndex = Number(field.replace('buy_ticket_', '')) - 1;
      if (Number.isNaN(ticketIndex) || ticketIndex < 0) {
        return;
      }

      const targetUnit = getBatchUnits(unitMap, data)[ticketIndex];
      if (!targetUnit) {
        return;
      }

      const parsedPrice = newValue === '' || newValue == null ? null : Number(newValue);
      if (newValue !== '' && newValue != null && Number.isNaN(parsedPrice)) {
        node.setDataValue(field, oldValue);
        setError('Ticket price must be a number.');
        return;
      }

      setError('');
      setMessage('');
      const result = await eventsService.updateUnit(targetUnit.unit_id, { unit_price: parsedPrice });
      if (!result.success) {
        node.setDataValue(field, oldValue);
        setError(result.error);
        return;
      }

      setUnitMap((current) => {
        const eventUnits = current[data.event_ref];
        if (!eventUnits?.items?.length) {
          return current;
        }

        const nextItems = eventUnits.items.map((item) => (
          item.unit_id === result.item.unit_id ? result.item : item
        ));

        return {
          ...current,
          [data.event_ref]: {
            ...eventUnits,
            items: nextItems,
          },
        };
      });

      setDetailsPanel((current) => {
        if (!current || current.batchCode !== data.source_uuid || current.eventRef !== data.event_ref) {
          return current;
        }
        const nextItems = current.items.map((item) => (
          item.unit_id === result.item.unit_id ? result.item : item
        ));
        return { ...current, items: nextItems };
      });

      setMessage('Ticket price updated.');
      return;
    } else if (field.startsWith('sell_ticket_')) {
      if (data?.row_type !== 'position') {
        return;
      }

      const ticketIndex = Number(field.replace('sell_ticket_', '')) - 1;
      if (Number.isNaN(ticketIndex) || ticketIndex < 0) {
        return;
      }

      const targetUnit = getSellColumnUnits(unitMap, data)[ticketIndex];
      if (!targetUnit) {
        return;
      }

      const parsedPrice = newValue === '' || newValue == null ? null : Number(newValue);
      if (newValue !== '' && newValue != null && Number.isNaN(parsedPrice)) {
        node.setDataValue(field, oldValue);
        setError('Sell price must be a number.');
        return;
      }

      setError('');
      setMessage('');
      const result = await eventsService.updateUnit(targetUnit.unit_id, { sell_unit_price: parsedPrice });
      if (!result.success) {
        node.setDataValue(field, oldValue);
        setError(result.error);
        return;
      }

      setUnitMap((current) => {
        const eventUnits = current[data.event_ref];
        if (!eventUnits?.items?.length) {
          return current;
        }

        const nextItems = eventUnits.items.map((item) => (
          item.unit_id === result.item.unit_id ? result.item : item
        ));

        return {
          ...current,
          [data.event_ref]: {
            ...eventUnits,
            items: nextItems,
          },
        };
      });

      setDetailsPanel((current) => {
        if (!current || current.batchCode !== data.source_uuid || current.eventRef !== data.event_ref) {
          return current;
        }
        const nextItems = current.items.map((item) => (
          item.unit_id === result.item.unit_id ? result.item : item
        ));
        return { ...current, items: nextItems };
      });

      setMessage('Sell price updated and ticket marked Sold.');
      return;
    } else {
      return;
    }

    setError('');
    setMessage('');
    const result = await eventsService.updateEvent(data.event_id, payload);
    if (!result.success) {
      node.setDataValue(field, oldValue);
      setError(result.error);
      return;
    }

    setItems((currentItems) => currentItems.map((item) => (
      item.event_id === result.item.event_id ? result.item : item
    )));
    node.setData({
      ...result.item,
      row_id: data.row_id,
      row_type: 'position',
      event_key: data.event_key,
      month_label: '',
      month_break: false,
      display_id: data.display_id,
    });
    setMessage('Event updated.');
  };

  const openTransferPanel = (rowData) => {
    if (!rowData?.event_ref || !rowData?.listing_ticket_type) {
      return;
    }

    setTransferPanel(rowData);
    setTransferForm({
      quantity: 1,
      operator: listingOptions.operators?.[0] || '',
      transferUserId: rowData.listing_transfer_users?.[0]?.user_id || '',
    });
  };

  const handleAddTransfer = async (event) => {
    event.preventDefault();
    if (!transferPanel) {
      return;
    }

    const quantity = Number(transferForm.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError('Transfer quantity must be a positive number.');
      return;
    }
    if (!transferForm.operator) {
      setError('Select an operator for the transfer.');
      return;
    }

    const transferUser = (transferPanel.listing_transfer_users || []).find(
      (item) => String(item.user_id || '') === String(transferForm.transferUserId || ''),
    );

    setError('');
    setMessage('');
    const result = await eventsService.createTransfer({
      event_ref: transferPanel.event_ref,
      ticket_type: transferPanel.listing_ticket_type,
      batch_code: transferPanel.source_uuid || transferPanel.batch_code || null,
      quantity,
      operator: transferForm.operator,
      transfer_user_id: transferForm.transferUserId || null,
      transfer_username: transferUser?.username || null,
    });

    if (!result.success) {
      setError(result.error);
      return;
    }

    applyTransferItem(result.item);
    await refreshEventUnits(result.item.event_ref || transferPanel.event_ref);
    setTransferPanel(null);
    setMessage('Transfer added.');
  };

  const handleCancelTransfer = async (transfer) => {
    if (!transfer?.transfer_id) {
      return;
    }

    const orderId = transfer.order_id || transfer.tikwiz_order_id;
    const confirmationMessage = orderId
      ? `Cancel the transfer for order ${orderId}? This will move the associated order back to "Retransfer" and return its delivered tickets to Sold.`
      : 'Cancel this manual transfer? Its delivered tickets will be returned to Sold.';
    if (!window.confirm(confirmationMessage)) {
      return;
    }

    setError('');
    setMessage('');
    const result = await eventsService.deleteTransfer(transfer.transfer_id);
    if (!result.success) {
      setError(result.error);
      return;
    }

    const deletedTransfer = result.item || transfer;
    removeTransferItem(deletedTransfer);
    await refreshEventUnits(deletedTransfer.event_ref || transferPanel?.event_ref);
    setTransferPanel((current) => {
      if (!current) return current;
      return {
        ...current,
        listing_transfers: (current.listing_transfers || []).filter(
          (item) => (
            deletedTransfer.tikwiz_order_id
              ? item.tikwiz_order_id !== deletedTransfer.tikwiz_order_id
              : item.transfer_id !== deletedTransfer.transfer_id
          ),
        ),
      };
    });
    setMessage(result.message || 'Transfer cancelled.');
  };

  const handleCellClicked = (params) => {
    if (
      activeView === 'listings'
      && ['listing_outstanding', 'listing_total_left'].includes(params.colDef.field)
      && params.data?.listing_can_edit
    ) {
      openTransferPanel(params.data);
      return;
    }

    if (
      activeView === 'pnl'
      && params.colDef.field === 'month_label'
      && params.data?.row_type === 'pnl_month_summary'
    ) {
      setCollapsedPnlMonths((current) => {
        const next = new Set(current);
        const monthKey = params.data.pnl_month_key;
        if (!monthKey) return next;
        if (next.has(monthKey)) {
          next.delete(monthKey);
        } else {
          next.add(monthKey);
        }
        return next;
      });
      return;
    }

    if (params.colDef.field === 'display_id' && params.data?.row_type === 'event') {
      setExpandedEventIds((current) => {
        const next = new Set(current);
        if (next.has(params.data.event_key)) {
          next.delete(params.data.event_key);
        } else {
          next.add(params.data.event_key);
        }
        return next;
      });
    }
  };

  const closeAdjustmentEditor = () => {
    if (adjustmentSaving) return;
    setAdjustmentPanel(null);
    setAdjustmentRows([]);
    setAdjustmentError('');
  };

  const openAdjustmentEditor = async (rowData) => {
    if (!rowData?.event_id) return;

    setAdjustmentPanel(rowData);
    setAdjustmentRows([]);
    setAdjustmentError('');
    setAdjustmentLoading(true);
    const result = await eventsService.getAdjustments(rowData.event_id);
    setAdjustmentLoading(false);

    if (!result.success) {
      setAdjustmentError(result.error);
      return;
    }

    const nextRows = (result.items || []).map(createAdjustmentRow);
    setAdjustmentRows(nextRows.length ? nextRows : [createAdjustmentRow()]);
  };

  const handleAdjustmentSubmit = async (event) => {
    event.preventDefault();
    if (!adjustmentPanel?.event_id) return;

    const populatedRows = adjustmentRows.filter((row) => (
      String(row.value || '').trim() || String(row.explanation || '').trim()
    ));
    const invalidRow = populatedRows.find((row) => (
      !String(row.value || '').trim() || !Number.isFinite(Number(row.value))
    ));
    if (invalidRow) {
      setAdjustmentError('Each adjustment needs a valid numeric Value.');
      return;
    }

    setAdjustmentSaving(true);
    setAdjustmentError('');
    const result = await eventsService.replaceAdjustments(
      adjustmentPanel.event_id,
      populatedRows.map((row) => ({
        value: Number(row.value),
        explanation: String(row.explanation || '').trim(),
      })),
    );
    setAdjustmentSaving(false);

    if (!result.success) {
      setAdjustmentError(result.error);
      return;
    }

    setItems((current) => current.map((item) => (
      item.event_id === adjustmentPanel.event_id
        ? { ...item, adjustment_total: result.total || 0 }
        : item
    )));
    setAdjustmentPanel(null);
    setAdjustmentRows([]);
    setMessage('Adjustments updated.');
  };

  const handleCellDoubleClicked = (params) => {
    if (!isDashboardRoute && params.colDef.field === 'artist' && params.data?.row_type === 'event') {
      const link = String(params.data?.primary_event_link || '').trim();
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        setEventLinkPanel(params.data);
        setEventLinkValue('');
        setEventLinkError('');
      }
      return;
    }

    if (
      activeView === 'unit'
      && params.colDef.field === 'adjustment_total'
      && params.data?.row_type === 'position'
    ) {
      openAdjustmentEditor(params.data);
      return;
    }

    if (activeView !== 'unit' || params.data?.row_type !== 'position') {
      return;
    }

    openTicketDetails(params.data);
  };

  const openTicketDetails = (rowData) => {
    const batchUnits = getBatchUnits(unitMap, rowData);
    setDetailsPanel(createBatchDetailsState(rowData, batchUnits));
    setContextMenu(null);
  };

  const copyOrderId = async (value) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(value));
      setError('');
      setMessage('order_id copied.');
    } catch (copyError) {
      setError('Failed to copy order_id.');
    }
  };

  const handleRestoreUnit = async (unit) => {
    const currentStatus = String(unit?.status || '').toLowerCase();
    if (!unit?.unit_id || !['expired', 'sold', 'delivered'].includes(currentStatus)) {
      return;
    }

    if (['sold', 'delivered'].includes(currentStatus) && !canRestoreSoldUnits) {
      setError('Only TS or DV can restore Sold or Delivered units to Available.');
      return;
    }

    if (
      ['sold', 'delivered'].includes(currentStatus)
      && !window.confirm(
        `Restore ${unit.unit_code || 'this unit'} to Available? Its order link and sell price will be removed.`,
      )
    ) {
      return;
    }

    setRestoringUnitId(unit.unit_id);
    setError('');
    setMessage('');
    const result = await eventsService.updateUnit(unit.unit_id, { status: 'Available' });
    setRestoringUnitId('');

    if (!result.success) {
      setError(result.error);
      return;
    }

    replaceUnitItem(result.item);
    setMessage(`${unit.unit_code || 'Unit'} restored to Available.`);
  };

  const openMoveOrder = async (rowData) => {
    setContextMenu(null);
    let nextUnitMap = unitMap;
    if (rowData?.event_ref && !unitMap[rowData.event_ref]) {
      const unitsResult = await refreshEventUnits(rowData.event_ref);
      if (!unitsResult) {
        setError('Failed to load units for the selected event.');
        return;
      }
      nextUnitMap = {
        ...unitMap,
        [rowData.event_ref]: unitsResult,
      };
    }

    const eventPositions = groupedEvents.find((item) => item.parent.event_ref === rowData?.event_ref)?.children || [];
    const orderOptions = getSoldOrderOptions(nextUnitMap, rowData, eventPositions);
    if (!orderOptions.length) {
      setError('Selected position does not have any sold orders to move.');
      return;
    }

    setError('');
    setMessage('');
    setMoveOrderPanel({ rowData, orderOptions });
    setMoveOrderForm({
      orderId: orderOptions[0].orderId,
      targetUserId: '',
      allowOverAvailability: false,
    });
    setMoveOrderWarning('');
  };

  const closeMoveOrder = () => {
    if (moveOrderSaving) {
      return;
    }
    setMoveOrderPanel(null);
    setMoveOrderWarning('');
    setMoveOrderForm({
      orderId: '',
      targetUserId: '',
      allowOverAvailability: false,
    });
  };

  const handleMoveOrderFieldChange = (field, value) => {
    setMoveOrderForm((current) => ({
      ...current,
      [field]: value,
      allowOverAvailability: false,
    }));
    setMoveOrderWarning('');
  };

  const handleMoveOrderSubmit = async (event) => {
    event.preventDefault();
    if (!moveOrderPanel || !selectedMoveOrder) {
      return;
    }
    if (!moveOrderForm.targetUserId) {
      setError('Select a target position for the move.');
      return;
    }

    if (!moveOrderForm.allowOverAvailability) {
      if (!moveTargetAvailability.hasPosition) {
        setMoveOrderWarning('A matching target position is not available. Add the position first, then move the order.');
        return;
      }
      if (!moveToVoidSelected && moveTargetAvailability.available < moveQuantityNeeded) {
        setMoveOrderWarning(
          `Target user has ${moveTargetAvailability.available} available tickets for the ${moveQuantityNeeded} tickets to move. Confirm to move anyway.`,
        );
        setMoveOrderForm((current) => ({ ...current, allowOverAvailability: true }));
        return;
      }
    }

    setMoveOrderSaving(true);
    setError('');
    setMessage('');
    const result = await eventsService.moveOrder(
      selectedMoveOrder.orderId,
      moveToVoidSelected
        ? { move_to_void: true }
        : {
          target_user_id: moveOrderForm.targetUserId,
          allow_over_availability: moveOrderForm.allowOverAvailability,
        },
    );

    if (!result.success) {
      setMoveOrderSaving(false);
      if (result.status === 409 && String(result.error || '').includes('Confirm to move anyway')) {
        setMoveOrderWarning(result.error);
        setMoveOrderForm((current) => ({ ...current, allowOverAvailability: true }));
      } else {
        setError(result.error);
      }
      return;
    }

    const eventRef = result.event_ref || moveOrderPanel.rowData.event_ref;
    await refreshEventUnits(eventRef);
    if (activeView === 'listings') {
      await loadListings([eventRef]);
    }
    if (showSeasonTickets) {
      await loadSeasonTicketEvents(selectedSeasonPackage);
    } else {
      await loadEvents(tab, query.trim());
    }

    setMoveOrderSaving(false);
    setMoveOrderPanel(null);
    setMoveOrderWarning('');
    setMessage(result.message || 'Order moved.');
  };

  const openUserHistory = async (rowData) => {
    if (!rowData?.event_id) {
      setError('Selected row does not have an event id.');
      setContextMenu(null);
      return;
    }

    setContextMenu(null);
    setHistoryLoading(true);
    setHistoryError('');
    setError('');
    setMessage('');

    const result = await eventsService.getUserHistory(rowData.event_id);
    if (!result.success) {
      setHistoryPanel(null);
      setHistoryError(result.error);
      setError(result.error);
      setHistoryLoading(false);
      return;
    }

    setHistoryPanel(createUserHistoryState(rowData, result));
    setHistoryLoading(false);
  };

  const openAddPosition = async (rowData) => {
    if (!rowData?.event_id) {
      setError('Selected row does not have an event id.');
      setContextMenu(null);
      return;
    }

    setError('');
    setMessage('');
    setAddPositionPanel(rowData);
    setAddPositionForm({
      isVoid: false,
      userId: '',
      quantity: '1',
      unitPrice: '0.00',
    });
    setAddPositionUserQuery('');
    setAddPositionUserOptions([]);
    setContextMenu(null);
    setAddPositionPrefillLoading(true);

    let sourceUnits = getBatchUnits(unitMap, rowData);
    if (rowData.event_ref && !unitMap[rowData.event_ref]) {
      const unitsResult = await refreshEventUnits(rowData.event_ref);
      if (!unitsResult) {
        setAddPositionPanel(null);
        setAddPositionPrefillLoading(false);
        setError('Failed to load the source position quantity and price.');
        return;
      }
      sourceUnits = (unitsResult?.items || [])
        .filter((unit) => unit.batch_code === rowData.source_uuid)
        .sort((left, right) => (left.unit_number || 0) - (right.unit_number || 0));
    }

    const quantity = sourceUnits.length || 1;
    const totalBuyPrice = sourceUnits.reduce((total, unit) => {
      const price = Number(unit.unit_price);
      return total + (Number.isFinite(price) ? price : 0);
    }, 0);
    const averageUnitPrice = sourceUnits.length ? totalBuyPrice / sourceUnits.length : 0;
    setAddPositionForm((current) => ({
      ...current,
      quantity: String(quantity),
      unitPrice: averageUnitPrice.toFixed(2),
    }));
    setAddPositionPrefillLoading(false);
  };

  const openAddPurchase = (rowData) => {
    if (!rowData?.event_id) {
      setError('Selected row does not have an event id.');
      setContextMenu(null);
      return;
    }

    setError('');
    setMessage('');
    setAddPurchasePanel(rowData);
    setAddPurchaseForm({
      quantity: '1',
      unitPrice: '',
      card: '',
    });
    setContextMenu(null);
  };

  const selectAddPositionUser = (item) => {
    setAddPositionForm((current) => ({
      ...current,
      userId: item.user_id,
    }));
    setAddPositionUserQuery(item.username || item.email || item.name || '');
    setAddPositionUserOptions([]);
  };

  const handleAddPositionSubmit = async (event) => {
    event.preventDefault();
    if (!addPositionPanel?.event_id) {
      setError('Selected row does not have an event id.');
      return;
    }

    if (!addPositionForm.isVoid && !addPositionForm.userId) {
      setError('Select a user or mark the new position as Void.');
      return;
    }

    const quantity = Number(addPositionForm.quantity);
    const unitPrice = Number(addPositionForm.unitPrice);
    if (!addPositionForm.isVoid && (!Number.isInteger(quantity) || quantity < 1)) {
      setError('Number of tickets must be a whole number greater than zero.');
      return;
    }
    if (!addPositionForm.isVoid && (
      addPositionForm.unitPrice === '' || !Number.isFinite(unitPrice) || unitPrice < 0
    )) {
      setError('Unit price must be zero or greater.');
      return;
    }

    setAddPositionLoading(true);
    setError('');
    setMessage('');

    const result = await eventsService.createPosition(addPositionPanel.event_id, {
      is_void: addPositionForm.isVoid,
      user_id: addPositionForm.isVoid ? null : addPositionForm.userId,
      quantity: addPositionForm.isVoid ? null : quantity,
      unit_price: addPositionForm.isVoid ? null : unitPrice,
    });

    if (!result.success) {
      setError(result.error);
      setAddPositionLoading(false);
      return;
    }

    const eventRef = result.item?.event_ref || addPositionPanel.event_ref;
    if (eventRef) {
      setUnitMap((current) => {
        const next = { ...current };
        delete next[eventRef];
        return next;
      });
    }

    setAddPositionPanel(null);
    setAddPositionLoading(false);
    setMessage(result.message || 'Position created successfully.');

    if (showSeasonTickets) {
      await loadSeasonTicketEvents(selectedSeasonPackage);
    } else {
      await loadEvents(tab, query.trim());
    }
  };

  const handleAddPurchaseSubmit = async (event) => {
    event.preventDefault();
    if (!addPurchasePanel?.event_id) {
      setError('Selected row does not have an event id.');
      return;
    }

    const quantity = Number(addPurchaseForm.quantity);
    const unitPrice = Number(addPurchaseForm.unitPrice);

    if (!Number.isInteger(quantity) || quantity <= 0) {
      setError('Quantity must be a whole number greater than zero.');
      return;
    }

    if (addPurchaseForm.unitPrice === '' || Number.isNaN(unitPrice) || unitPrice < 0) {
      setError('Price must be zero or greater.');
      return;
    }

    setAddPurchaseLoading(true);
    setError('');
    setMessage('');

    const result = await eventsService.addPurchaseToPosition(addPurchasePanel.event_id, {
      quantity,
      unit_price: unitPrice,
      card: addPurchaseForm.card.trim() || null,
    });

    if (!result.success) {
      setError(result.error);
      setAddPurchaseLoading(false);
      return;
    }

    const eventRef = result.item?.event_ref || addPurchasePanel.event_ref;
    await refreshEventUnits(eventRef);
    if (activeView === 'listings') {
      await loadListings([eventRef]);
    }
    if (showSeasonTickets) {
      await loadSeasonTicketEvents(selectedSeasonPackage);
    } else {
      await loadEvents(tab, query.trim());
    }

    setAddPurchasePanel(null);
    setAddPurchaseLoading(false);
    setMessage(result.message || 'Purchase added successfully.');
  };

  const handleCellContextMenu = (params) => {
    if (!isDashboardRoute && params.colDef.field === 'artist' && params.data?.row_type === 'event') {
      params.event.preventDefault();
      setContextMenu({
        x: params.event.clientX,
        y: params.event.clientY,
        rowData: params.data,
        eventLinkOnly: true,
      });
      return;
    }

    if (params.data?.row_type !== 'position') {
      if (!canExport) {
        setContextMenu(null);
        return;
      }
      params.event.preventDefault();
      setContextMenu({
        x: params.event.clientX,
        y: params.event.clientY,
        rowData: params.data,
        exportOnly: true,
      });
      return;
    }

    params.event.preventDefault();
    setContextMenu({
      x: params.event.clientX,
      y: params.event.clientY,
      rowData: params.data,
    });
  };

  const openEventLinkEditor = (rowData) => {
    setContextMenu(null);
    setEventLinkPanel(rowData);
    setEventLinkValue(String(rowData?.primary_event_link || ''));
    setEventLinkError('');
  };

  const closeEventLinkEditor = () => {
    if (eventLinkSaving) return;
    setEventLinkPanel(null);
    setEventLinkError('');
  };

  const handleEventLinkSubmit = async (event) => {
    event.preventDefault();
    const link = eventLinkValue.trim();
    if (link && !/^https?:\/\//i.test(link)) {
      setEventLinkError('Link must start with http:// or https://.');
      return;
    }

    const targets = eventLinkPanel?.row_type === 'event'
      ? items.filter((item) => getEventGroupKey(item) === eventLinkPanel.event_key)
      : items.filter((item) => item.event_id === eventLinkPanel?.event_id);
    if (!targets.length) {
      setEventLinkError('No event rows were found to update.');
      return;
    }

    setEventLinkSaving(true);
    setEventLinkError('');
    const updatedItems = [];
    for (const target of targets) {
      const result = await eventsService.updateEvent(target.event_id, { primary_event_link: link });
      if (!result.success) {
        setEventLinkError(result.error);
        setEventLinkSaving(false);
        return;
      }
      updatedItems.push(result.item);
    }
    setItems((current) => current.map((item) => (
      updatedItems.find((updated) => updated.event_id === item.event_id) || item
    )));
    setEventLinkSaving(false);
    setEventLinkPanel(null);
    setMessage(link ? 'Primary market link updated.' : 'Primary market link removed.');
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const qValue = queryParams.get('q') || '';
    const tabValue = queryParams.get('tab') || '';
    const monthValue = queryParams.get('month') || '';
    const viewValue = queryParams.get('view') || '';
    const yearValue = normalizeEventYear(queryParams.get('year'));
    setQuery(qValue);
    setTab(tabValue);
    setSelectedYear(yearValue);
    if (yearValue < CURRENT_EVENT_YEAR) setUpcomingOnly(false);
    setMonthFilter(monthValue);
    setDateRange({ start: '', end: '' });
    if (['general', 'pnl', 'unit', 'listings'].includes(viewValue)) {
      if (viewValue === 'pnl' && !canSeePnl) {
        setActiveView('general');
        loadEvents(tabValue, qValue, yearValue);
        return;
      }
      setActiveView(viewValue);
      if (viewValue === 'pnl' || viewValue === 'listings') {
        setUpcomingOnly(false);
      }
    }
    loadEvents(tabValue, qValue, yearValue);
  }, [location.search, canSeePnl]);

  useEffect(() => {
    if (!contextMenu) {
      return undefined;
    }

    const closeMenu = () => setContextMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);

    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [contextMenu]);

  useEffect(() => {
    const eventRefs = groupedEvents.map((group) => group.parent.event_ref);
    if (activeView === 'unit' || activeView === 'pnl' || activeView === 'listings') {
      loadUnits(eventRefs);
      if (activeView === 'listings' || activeView === 'unit') {
        loadListings(eventRefs);
      }
      if (activeView !== 'listings') {
        setTransferPanel(null);
      }
    } else {
      setContextMenu(null);
      setTransferPanel(null);
      setMoveOrderPanel(null);
      setMoveOrderWarning('');
    }
  }, [activeView, groupedEvents]);

  useEffect(() => {
    const pollEventsPage = async () => {
      if (document.visibilityState === 'hidden' || pollRefreshInFlightRef.current) {
        return;
      }

      pollRefreshInFlightRef.current = true;
      try {
        if (displayCurrency) {
          const ratesResult = await eventsService.getCurrencyRates();
          setCurrencySnapshot(ratesResult.success ? ratesResult : null);
        }
        const displayResult = await eventsService.getDisplay({
          tab: showSeasonTickets ? selectedSeasonPackage?.sourceTab : (tab || undefined),
          q: showSeasonTickets ? undefined : (query.trim() || undefined),
          artist: showSeasonTickets ? selectedSeasonPackage?.name : undefined,
          year: showSeasonTickets ? undefined : selectedYear,
          startDate: showSeasonTickets ? selectedSeasonPackage?.startDate : undefined,
          endDate: showSeasonTickets ? selectedSeasonPackage?.endDate : undefined,
          limit: 2000,
        });

        if (displayResult.success) {
          setItems(displayResult.items || []);
          setTotalCount(displayResult.totalCount || 0);
        }

        const eventRefs = groupedEvents
          .map((group) => group.parent.event_ref)
          .filter(Boolean);

        if ((activeView === 'unit' || activeView === 'pnl' || activeView === 'listings') && eventRefs.length) {
          const unitResults = await Promise.all(eventRefs.map((eventRef) => eventsService.getUnits(eventRef)));
          const nextUnitMap = {};

          unitResults.forEach((result, index) => {
            if (!result.success) {
              return;
            }
            nextUnitMap[eventRefs[index]] = result;
          });

          if (Object.keys(nextUnitMap).length) {
            setUnitMap((current) => ({ ...current, ...nextUnitMap }));
            setDetailsPanel((current) => {
              if (!current?.eventRef || !nextUnitMap[current.eventRef]) {
                return current;
              }
              return {
                ...current,
                items: (nextUnitMap[current.eventRef].items || []).filter(
                  (item) => item.batch_code === current.batchCode,
                ),
              };
            });
          }

          if (activeView === 'listings') {
            const listingsResult = await eventsService.getListings(eventRefs);
            if (listingsResult.success) {
              const nextListingMap = {};
              (listingsResult.items || []).forEach((item) => {
                nextListingMap[buildListingKey(item.event_ref, item.ticket_type, item.listing_slot)] = item;
              });

              const nextTransferMap = {};
              (listingsResult.transfers || []).forEach((item) => {
                const key = buildTransferKey(item.event_ref, item.ticket_type);
                nextTransferMap[key] = [...(nextTransferMap[key] || []), item];
              });

              setListingMap(nextListingMap);
              setTransferMap(nextTransferMap);
            }
          }
        }
      } finally {
        pollRefreshInFlightRef.current = false;
      }
    };

    let intervalId = window.setInterval(() => {
      pollEventsPage();
    }, EVENTS_POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      window.clearInterval(intervalId);
      pollEventsPage();
      intervalId = window.setInterval(() => {
        pollEventsPage();
      }, EVENTS_POLL_INTERVAL_MS);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeView, groupedEvents, query, selectedSeasonPackage, selectedYear, showSeasonTickets, tab, displayCurrency]);

  const handleFilter = (event) => {
    event.preventDefault();
    if (showSeasonTickets) {
      loadSeasonTicketEvents(selectedSeasonPackage);
      return;
    }
    const nextParams = new URLSearchParams(location.search);
    const trimmedQuery = query.trim();
    if (trimmedQuery) nextParams.set('q', trimmedQuery);
    else nextParams.delete('q');
    if (tab) nextParams.set('tab', tab);
    else nextParams.delete('tab');
    nextParams.set('year', String(selectedYear));
    const nextSearch = nextParams.toString();
    if (nextSearch === location.search.replace(/^\?/, '')) {
      loadEvents(tab, trimmedQuery, selectedYear);
    } else {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }
  };

  const handleSelectSeasonPackage = (packageId) => {
    const nextPackage = SEASON_TICKET_PACKAGES.find((item) => item.id === packageId) || SEASON_TICKET_PACKAGES[0];
    setSelectedSeasonPackageId(nextPackage.id);
    setQuery('');
    setMonthFilter('');
    setDateRange({ start: '', end: '' });
    loadSeasonTicketEvents(nextPackage);
  };

  const handleToggleSeasonTickets = () => {
    const nextShowSeasonTickets = !showSeasonTickets;
    setShowSeasonTickets(nextShowSeasonTickets);
    setTransferPanel(null);
    setDetailsPanel(null);
    setHistoryPanel(null);
    setHistoryError('');
    setMoveOrderPanel(null);
    setMoveOrderWarning('');
    setContextMenu(null);

    if (nextShowSeasonTickets) {
      setUpcomingOnly(false);
      setQuery('');
      setMonthFilter('');
      setDateRange({ start: '', end: '' });
      loadSeasonTicketEvents(selectedSeasonPackage);
    } else {
      loadEvents(tab, query.trim());
    }
  };

  const manualEntryPath = location.pathname.startsWith('/dashboard')
    ? '/dashboard/manual-entry?origin=events&returnTo=/dashboard/events'
    : '/user/manual-entry?origin=events&returnTo=/user/events';
  const dashboardPath = location.pathname.startsWith('/dashboard') ? '/dashboard' : '/user';
  const financialsPath = location.pathname.startsWith('/dashboard')
    ? '/dashboard/financials'
    : '/user/financials';

  return (
    <div className={`events-page ${showSeasonTickets ? 'resource-page-with-sidebar events-page-season-mode' : ''}`}>
      {showSeasonTickets && (
        <SeasonTicketsSidebar
          packages={SEASON_TICKET_PACKAGES}
          selectedPackageId={selectedSeasonPackageId}
          onSelectPackage={handleSelectSeasonPackage}
        />
      )}
      <header className="events-header">
        <div className="events-header-content">
          <HeaderNewTabButton />
          <div>
            <h1>{showSeasonTickets ? 'Season Tickets' : 'Events Display'}</h1>
          </div>
          <div className="events-header-actions">
            <button className="btn btn-primary" type="button" onClick={() => navigate(manualEntryPath)}>
              Manual Entry
            </button>
            <button className="btn btn-secondary" onClick={() => navigate(dashboardPath)}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="events-main">
        <form className={`events-filter-form ${showSeasonTickets ? 'events-filter-form-season' : ''}`} onSubmit={handleFilter}>
          {!showSeasonTickets && (
            <YearSelector
              id="events-event-year"
              value={selectedYear}
              onChange={(nextYear) => {
                const nextParams = new URLSearchParams(location.search);
                const trimmedQuery = query.trim();
                if (trimmedQuery) nextParams.set('q', trimmedQuery);
                else nextParams.delete('q');
                if (tab) nextParams.set('tab', tab);
                else nextParams.delete('tab');
                nextParams.set('year', String(nextYear));
                navigate({ pathname: location.pathname, search: nextParams.toString() }, { replace: true });
              }}
            />
          )}
          <div className="form-group events-date-form-group">
            <label htmlFor="events-date-range">Date range</label>
            <DateRangePicker
              value={dateRange}
              legacyMonth={monthFilter}
              onChange={(nextRange) => {
                setDateRange(nextRange);
                setMonthFilter('');
                if (nextRange.start) setUpcomingOnly(false);
              }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="events-query">Search</label>
            <input
              id="events-query"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="event, artist, venue, user..."
            />
          </div>
          <div className="form-group">
            <label htmlFor="events-tab">TWZ Location</label>
            <select
              id="events-tab"
              value={tab}
              onChange={(e) => setTab(e.target.value)}
            >
              <option value="">All</option>
              {EVENT_TWZ_LOCATION_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="events-filter-actions">
            <button className="btn btn-primary" type="submit">
              Apply
            </button>
            <button
              className={`btn ${upcomingOnly ? 'btn-primary' : 'btn-secondary'} events-toggle-btn`}
              type="button"
              onClick={() => setUpcomingOnly((current) => !current)}
            >
              {upcomingOnly ? 'Upcoming Only' : 'All Events'}
            </button>
            <button
              type="button"
              className="btn btn-secondary events-toggle-btn"
              onClick={handleToggleAllPositions}
              disabled={!groupedEvents.length}
            >
              {allPositionsExpanded ? 'Collapse All Positions' : 'Expand All Positions'}
            </button>
          </div>
        </form>

            {displayCurrency && activeView !== 'pnl' && (
              <p className="events-currency-note">Amounts displayed in {displayCurrency}. Buy/Sell edits use original transaction currencies; hover for details.</p>
            )}
        <div className="events-summary">
          <strong>Showing:</strong> {groupedEvents.length} events / {gridRows.length} rows
          {!showSeasonTickets && <span className="events-summary-tag">Event year: {selectedYear}</span>}
          {showSeasonTickets && selectedSeasonPackage && (
            <>
              <span className="events-summary-tag">Team: {selectedSeasonPackage.name}</span>
              <span className="events-summary-tag">Season: {selectedSeasonPackage.season}</span>
            </>
          )}
          {upcomingOnly && <span className="events-summary-tag">Upcoming only</span>}
          {monthFilter && <span className="events-summary-tag">Month: {monthFilter}</span>}
          {dateRange.start && (
            <span className="events-summary-tag">
              Dates: {formatRangeDate(dateRange.start)}{dateRange.end && dateRange.end !== dateRange.start ? ` – ${formatRangeDate(dateRange.end)}` : ''}
            </span>
          )}
          {!!totalCount && <span className="events-summary-tag">API rows: {totalCount}</span>}
        </div>

        {error && <div className="notification error">{error}</div>}
        {message && <div className="notification success">{message}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading events...</p>
          </div>
        ) : (
          <div className={showSeasonTickets ? 'events-season-page-layout' : 'events-grid-mode-layout'}>
            <div className="events-grid-shell">
            <div className="events-grid-note events-views-bar">
              <div className="events-view-buttons">
                {SHOW_GENERAL_VIEW_BUTTON && (
                  <button
                    type="button"
                    className={`btn ${activeView === 'general' ? 'btn-primary' : 'btn-secondary'} events-view-btn`}
                    onClick={() => {
                      setActiveView('general');
                    }}
                  >
                    General
                  </button>
                )}
                {canSeePnl && (
                  <button
                    type="button"
                    className={`btn ${activeView === 'pnl' ? 'btn-primary' : 'btn-secondary'} events-view-btn`}
                    onClick={() => {
                      setActiveView('pnl');
                    }}
                  >
                    PnL
                  </button>
                )}
                <button
                  type="button"
                  className={`btn ${activeView === 'listings' ? 'btn-primary' : 'btn-secondary'} events-view-btn`}
                  onClick={() => {
                    setActiveView('listings');
                  }}
                >
                  Listings
                </button>
                <button
                  type="button"
                  className={`btn ${activeView === 'unit' ? 'btn-primary' : 'btn-secondary'} events-view-btn`}
                  onClick={() => {
                    setActiveView('unit');
                  }}
                >
                  Unit
                </button>
              </div>
              <div className="events-view-actions">
                {activeView === 'pnl' && (
                  <button
                    type="button"
                    className="btn btn-secondary events-financials-btn"
                    onClick={() => navigate(financialsPath)}
                  >
                    Financials
                  </button>
                )}
                <button
                  type="button"
                  className={`btn ${showSeasonTickets ? 'btn-primary' : 'btn-secondary'} events-season-btn`}
                  onClick={handleToggleSeasonTickets}
                >
                  Season Mode
                </button>
              </div>
            </div>
            {activeView === 'pnl' && !tab && isCurrencyLocationView && (
              <section className="events-original-currency-summary" aria-label="PnL by original transaction currency">
                <strong>P/L by original transaction currency</strong>
                <p>For events selected by the page filters, including adjustments (in their purchase currency). No currency conversion.</p>
                {unitsLoading ? <p>Loading amounts...</p> : unitsError ? <p>Amounts unavailable: {unitsError}</p> : (
                  <>
                    <table>
                      <thead><tr><th scope="col">Currency</th><th scope="col">Cost</th><th scope="col">Revenue</th><th scope="col">Adjustments</th><th scope="col">P/L</th></tr></thead>
                      <tbody>
                        {originalCurrencySummary.rows.map((row) => (
                          <tr key={row.currency}>
                            <th scope="row">{row.currency}</th>
                            <td>{formatPnlMoney(row.cost)}</td><td>{formatPnlMoney(row.revenue)}</td><td>{formatPnlMoney(row.adjustments)}</td>
                            <td className={getPnlToneClass(row.pnl)}>{formatPnlMoney(row.pnl)}</td>
                          </tr>
                        ))}
                        {!originalCurrencySummary.rows.length && <tr><td colSpan={5}>No amounts with a known currency.</td></tr>}
                      </tbody>
                    </table>
                    {originalCurrencySummary.assumptions.length > 0 && (
                      <details>
                        <summary>Currency assumed from TWZ Location for {originalCurrencySummary.assumptions.length} amounts</summary>
                        <p><strong>Original transaction currency:</strong> total purchases and sales separately within USD, EUR, GBP, CHF, PLN, etc. A purchase and sale in different currencies would contribute to different rows.</p>
                        <ul>{originalCurrencySummary.assumptions.map((issue, index) => <li key={index}>{issue}</li>)}</ul>
                      </details>
                    )}
                    {originalCurrencySummary.unknownAmounts > 0 && (
                      <p role="status">Partial totals: {originalCurrencySummary.unknownAmounts} amounts with unknown currency or invalid value excluded.</p>
                    )}
                  </>
                )}
              </section>
            )}
            <div className="events-content-shell">
              <div className="ag-theme-quartz-dark events-ag-grid">
                <AgGridReact
                  ref={gridRef}
                  theme="legacy"
                  rowData={gridRows}
                  columnDefs={visibleColumnDefs}
                  defaultColDef={defaultColDef}
                  animateRows={true}
                  alwaysShowHorizontalScroll={true}
                  headerHeight={34}
                  floatingFiltersHeight={26}
                  tooltipShowDelay={250}
                  tooltipHideDelay={8000}
                  tooltipInteraction={true}
                  pagination={true}
                  paginationPageSize={100}
                  rowSelection={{
                    mode: 'singleRow',
                    checkboxes: false,
                    headerCheckbox: false,
                    enableClickSelection: true,
                  }}
                  onCellValueChanged={handleCellValueChanged}
                  onCellClicked={handleCellClicked}
                  onCellDoubleClicked={handleCellDoubleClicked}
                  onCellContextMenu={handleCellContextMenu}
                  preventDefaultOnContextMenu
                  getRowId={(params) => params.data.row_id}
                  rowClassRules={{
                    'events-month-break-row': (params) => Boolean(params.data?.month_break),
                    'events-position-row': (params) => params.data?.row_type === 'position',
                    'events-parent-row': (params) => params.data?.row_type === 'event',
                    'events-pnl-row': (params) => params.data?.row_type === 'pnl',
                    'events-pnl-month-summary-row': (params) => params.data?.row_type === 'pnl_month_summary',
                    'events-listings-row': (params) => activeView === 'listings',
                  }}
                />
              </div>
            </div>
            {(activeView === 'unit' || activeView === 'pnl' || activeView === 'listings') && (
              <div className="events-grid-note events-unit-inline-note">
                {(unitsLoading || (activeView === 'listings' && listingsLoading)) && (
                  <span>
                    {activeView === 'pnl' && 'Loading PnL data...'}
                    {activeView === 'unit' && 'Loading unit columns...'}
                    {activeView === 'listings' && 'Loading listings data...'}
                  </span>
                )}
                {!unitsLoading && !unitsError && activeView === 'unit' && (
                  <span>Double-click Adjustments to edit them, or another batch cell for Ticket Details. Right-click opens the action menu.</span>
                )}
                {!unitsLoading && !unitsError && activeView === 'pnl' && (
                  <span>Profit/Loss uses sold revenue minus total buy cost. ROI is Profit/Loss divided by total buy cost.</span>
                )}
                {!unitsLoading && !listingsLoading && !unitsError && !listingsError && activeView === 'listings' && (
                  <span>Click Outstanding or Total Left to view transfers.</span>
                )}
                {unitsError && <span className="events-unit-inline-error">{unitsError}</span>}
                {listingsError && <span className="events-unit-inline-error">{listingsError}</span>}
              </div>
            )}
            {transferPanel && (
              <aside className="events-unit-panel events-floating-panel events-transfer-panel">
                <div className="events-unit-panel-header">
                  <div>
                    <h2>Transfers</h2>
                    <p>
                      <span>{transferPanel.artist || transferPanel.event_name || 'Event'}</span>
                      <span>{transferPanel.listing_ticket_type}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary events-unit-close-btn"
                    onClick={() => setTransferPanel(null)}
                  >
                    Close
                  </button>
                </div>
                <form className="events-transfer-form" onSubmit={handleAddTransfer}>
                  <div className="events-transfer-existing">
                    <strong>Existing transfers</strong>
                    {transferPanel.listing_transfers?.length ? (
                      transferPanel.listing_transfers.map((transfer) => (
                        <div key={transfer.transfer_id} className="events-transfer-existing-row">
                          <span className="events-transfer-order-id">
                            {transfer.order_id || 'Manual'}
                          </span>
                          <span>{transfer.transfer_username || 'Unknown user'}</span>
                          <span>{transfer.operator}</span>
                          <strong>{transfer.quantity}</strong>
                          <button
                            type="button"
                            className="events-transfer-cancel-btn"
                            onClick={() => handleCancelTransfer(transfer)}
                          >
                            Cancel
                          </button>
                        </div>
                      ))
                    ) : (
                      <span>No transfers recorded.</span>
                    )}
                  </div>
                  <details className="events-transfer-manual-fallback">
                    <summary>Add manual transfer</summary>
                    <div className="events-transfer-manual-fields">
                      <label>
                        <span>Qty</span>
                        <input
                          type="number"
                          min="1"
                          value={transferForm.quantity}
                          onChange={(event) => setTransferForm((current) => ({
                            ...current,
                            quantity: event.target.value,
                          }))}
                        />
                      </label>
                      <label>
                        <span>Operator</span>
                        <select
                          value={transferForm.operator}
                          onChange={(event) => setTransferForm((current) => ({
                            ...current,
                            operator: event.target.value,
                          }))}
                        >
                          <option value="">Select</option>
                          {(listingOptions.operators || []).map((operator) => (
                            <option key={operator} value={operator}>{operator}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>User</span>
                        <select
                          value={transferForm.transferUserId}
                          onChange={(event) => setTransferForm((current) => ({
                            ...current,
                            transferUserId: event.target.value,
                          }))}
                        >
                          <option value="">Select</option>
                          {(transferPanel.listing_transfer_users || []).map((item) => (
                            <option key={item.user_id || item.username} value={item.user_id || ''}>
                              {item.username}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button type="submit" className="btn btn-primary events-transfer-submit">
                        Add Manual Transfer
                      </button>
                    </div>
                  </details>
                </form>
              </aside>
            )}
            {currencyPanel && (
              <aside className="events-unit-panel events-floating-panel events-details-panel events-currency-panel" role="dialog" aria-label="Currency details">
                <div className="events-unit-panel-header">
                  <h2>{currencyPanel.title} — Currency details</h2>
                  <button type="button" className="btn btn-secondary" onClick={() => setCurrencyPanel(null)}>Close</button>
                </div>
                <ul>{currencyPanel.issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
              </aside>
            )}
            {detailsPanel && (
              <aside className="events-unit-panel events-floating-panel events-details-panel">
                <div className="events-unit-panel-header">
                  <div>
                    <h2>{detailsPanel.eventName || 'Ticket Details'}</h2>
                    <p>
                      <span>{detailsPanel.eventDate ? formatEventDate(detailsPanel.eventDate) : ''}</span>
                      {detailsPanel.venue ? <span>{detailsPanel.venue}</span> : null}
                      {detailsPanel.vendor ? <span>{detailsPanel.vendor}</span> : null}
                      {detailsPanel.platform ? <span>{detailsPanel.platform}</span> : null}
                    </p>
                  </div>
                  <div className="events-unit-panel-actions">
                    <div className="events-unit-panel-meta">
                      <span className="events-unit-panel-ref">{detailsPanel.batchCode || detailsPanel.eventRef || '-'}</span>
                      <span className="events-unit-panel-count">{detailsPanel.items.length} units</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary events-unit-close-btn"
                      onClick={() => setDetailsPanel(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>

                {!detailsPanel.items.length ? (
                  <div className="events-unit-panel-empty">No units found for this batch.</div>
                ) : (
                  <div className="events-unit-price-groups">
                    <section className="events-unit-price-group">
                      <header className="events-unit-price-header">
                        <span className="events-unit-price-label">{detailsPanel.batchCode || detailsPanel.eventRef}</span>
                        <span className="events-unit-price-count">{detailsPanel.items.length} units</span>
                      </header>
                      <div className="events-unit-table">
                        <div className="events-unit-table-row events-unit-table-head">
                          <span>Unit</span>
                          <span>Status</span>
                          <span>Order</span>
                          <span>Card</span>
                          <span>Buy</span>
                          <span>Sell</span>
                          <span>Source</span>
                        </div>
                        {detailsPanel.items.map((item) => {
                          const displayOrderId = item.order_id || item.tikwiz_order_id;
                          const normalizedStatus = String(item.status || '').toLowerCase();
                          const isRestorable = normalizedStatus === 'expired' || (
                            canRestoreSoldUnits && ['sold', 'delivered'].includes(normalizedStatus)
                          );
                          const isRestoring = restoringUnitId === item.unit_id;
                          return (
                            <div key={item.unit_id} className="events-unit-table-row">
                              <span className="events-unit-code">{item.unit_code}</span>
                              <span>
                                {isRestorable ? (
                                  <button
                                    type="button"
                                    className={`events-unit-status events-unit-status-${normalizedStatus} events-unit-status-action`}
                                    onClick={() => handleRestoreUnit(item)}
                                    disabled={isRestoring}
                                    title="Restore to Available"
                                  >
                                    {isRestoring ? 'Restoring' : item.status}
                                  </button>
                                ) : (
                                  <span className={`events-unit-status events-unit-status-${item.status.toLowerCase()}`}>
                                    {item.status}
                                  </span>
                                )}
                              </span>
                              <span>
                                {displayOrderId ? (
                                  <button
                                    type="button"
                                    className="events-unit-order-info"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      copyOrderId(displayOrderId);
                                    }}
                                    title="Copy order_id"
                                  >
                                    {displayOrderId}
                                    <span className="events-unit-order-tooltip">
                                      <strong>{displayOrderId}</strong>
                                      <span>{item.order_sale_point || 'Unknown sale point'}</span>
                                    </span>
                                  </button>
                                ) : '-'}
                              </span>
                              <span>{item.card || '-'}</span>
                              <span className="events-unit-detail-price">{formatUnitPrice(displayBuy((unitMap[detailsPanel.eventRef]?.items || []).find((unit) => unit.unit_id === item.unit_id) || item))}</span>
                              <span className="events-unit-detail-sell-price">{formatUnitPrice(displaySell((unitMap[detailsPanel.eventRef]?.items || []).find((unit) => unit.unit_id === item.unit_id) || item))}</span>
                              <span>{getUnitSourceLabel(item)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  </div>
                )}
              </aside>
            )}
            {(historyLoading || historyPanel || historyError) && (
              <aside className="events-unit-panel events-floating-panel events-history-panel">
                <div className="events-unit-panel-header">
                  <div>
                    <h2>User History</h2>
                    <p>
                      {historyPanel?.username ? <span>{historyPanel.username}</span> : null}
                      {historyPanel?.assignedUserRaw ? <span>Raw: {historyPanel.assignedUserRaw}</span> : null}
                      {historyPanel?.eventName ? <span>From: {historyPanel.eventName}</span> : null}
                    </p>
                  </div>
                  <div className="events-unit-panel-actions">
                    <div className="events-unit-panel-meta">
                      {historyPanel ? (
                        <>
                          <span className="events-unit-panel-count">{historyPanel.futureCount} future</span>
                          <span className="events-unit-panel-count">{historyPanel.pastCount} past</span>
                        </>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary events-unit-close-btn"
                      onClick={() => {
                        setHistoryPanel(null);
                        setHistoryError('');
                        setHistoryLoading(false);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="events-unit-panel-empty">Loading user history...</div>
                ) : null}

                {!historyLoading && historyError ? (
                  <div className="events-unit-panel-empty events-history-error">{historyError}</div>
                ) : null}

                {!historyLoading && !historyError && historyPanel ? (
                  <div className="events-unit-price-groups">
                    <section className="events-unit-price-group">
                      <header className="events-unit-price-header">
                        <span className="events-unit-price-label">Future Events</span>
                        <span className="events-unit-price-count">{historyPanel.futureCount} events</span>
                      </header>
                      {!historyPanel.futureItems.length ? (
                        <div className="events-unit-panel-empty">No future events assigned to this user.</div>
                      ) : (
                        <div className="events-history-list">
                          {historyPanel.futureItems.map((item) => (
                            <article key={item.event_id} className="events-history-card">
                              <div className="events-history-card-top">
                                <span className="events-history-ref">{item.event_ref || item.source_uuid || '-'}</span>
                                <span className="events-history-date">{item.event_date ? formatEventDate(item.event_date) : 'No date'}</span>
                              </div>
                              <h3>{item.event_name}</h3>
                              <p>
                                {item.artist ? <span>{item.artist}</span> : null}
                                {item.ticket_type ? <span>{item.ticket_type}</span> : null}
                                {item.venue ? <span>{item.venue}</span> : null}
                                {item.location ? <span>{item.location}</span> : null}
                                {item.platform ? <span>{item.platform}</span> : null}
                              </p>
                            </article>
                          ))}
                        </div>
                      )}
                    </section>

                    <section className="events-unit-price-group">
                      <header className="events-unit-price-header">
                        <span className="events-unit-price-label">Past Events</span>
                        <span className="events-unit-price-count">{historyPanel.pastCount} events</span>
                      </header>
                      {!historyPanel.pastItems.length ? (
                        <div className="events-unit-panel-empty">No past events assigned to this user.</div>
                      ) : (
                        <div className="events-history-list">
                          {historyPanel.pastItems.map((item) => (
                            <article key={item.event_id} className="events-history-card">
                              <div className="events-history-card-top">
                                <span className="events-history-ref">{item.event_ref || item.source_uuid || '-'}</span>
                                <span className="events-history-date">{item.event_date ? formatEventDate(item.event_date) : 'No date'}</span>
                              </div>
                              <h3>{item.event_name}</h3>
                              <p>
                                {item.artist ? <span>{item.artist}</span> : null}
                                {item.ticket_type ? <span>{item.ticket_type}</span> : null}
                                {item.venue ? <span>{item.venue}</span> : null}
                                {item.location ? <span>{item.location}</span> : null}
                                {item.platform ? <span>{item.platform}</span> : null}
                              </p>
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                ) : null}
              </aside>
            )}
            {addPositionPanel && (
              <aside className="events-unit-panel events-floating-panel events-add-position-panel">
                <div className="events-unit-panel-header">
                  <div>
                    <h2>Add New Position</h2>
                    <p>
                      <span>{addPositionPanel.source_uuid || addPositionPanel.event_ref || 'Position'}</span>
                      <span>{addPositionPanel.artist || addPositionPanel.event_name || 'Event'}</span>
                      {addPositionPanel.ticket_type ? <span>{addPositionPanel.ticket_type}</span> : null}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary events-unit-close-btn"
                    onClick={() => {
                      setAddPositionPanel(null);
                      setAddPositionLoading(false);
                      setAddPositionPrefillLoading(false);
                    }}
                  >
                    Close
                  </button>
                </div>
                <form className="events-add-position-form" onSubmit={handleAddPositionSubmit}>
                  <label className="events-add-position-void">
                    <input
                      type="checkbox"
                      checked={addPositionForm.isVoid}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setAddPositionForm((current) => ({
                          ...current,
                          isVoid: checked,
                          userId: checked ? '' : current.userId,
                        }));
                        if (checked) {
                          setAddPositionUserQuery('');
                          setAddPositionUserOptions([]);
                        }
                      }}
                    />
                    <span>Void position</span>
                  </label>

                  <label>
                    <span>User</span>
                    <input
                      type="text"
                      value={addPositionUserQuery}
                      disabled={addPositionForm.isVoid}
                      onChange={(event) => {
                        setAddPositionUserQuery(event.target.value);
                        setAddPositionForm((current) => ({ ...current, userId: '' }));
                      }}
                      placeholder={addPositionForm.isVoid ? 'No user needed for Void' : 'Search user...'}
                    />
                  </label>

                  {!addPositionForm.isVoid && (
                    <div className="events-add-position-user-list">
                      {addPositionSearching ? (
                        <div className="events-add-position-user-empty">Searching...</div>
                      ) : null}
                      {!addPositionSearching && addPositionUserQuery.trim() && !addPositionUserOptions.length && !addPositionForm.userId ? (
                        <div className="events-add-position-user-empty">No users found.</div>
                      ) : null}
                      {addPositionUserOptions.map((item) => (
                        <button
                          key={item.user_id}
                          type="button"
                          className="events-add-position-user-option"
                          onClick={() => selectAddPositionUser(item)}
                        >
                          <strong>{item.username}</strong>
                          <span>{item.name || item.email || ''}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="events-add-position-purchase-fields">
                    <label>
                      <span>Number of Tickets</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={addPositionForm.quantity}
                        onChange={(event) => setAddPositionForm((current) => ({
                          ...current,
                          quantity: event.target.value,
                        }))}
                        disabled={addPositionForm.isVoid || addPositionPrefillLoading || addPositionLoading}
                      />
                    </label>
                    <label>
                      <span>Unit Price</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={addPositionForm.unitPrice}
                        onChange={(event) => setAddPositionForm((current) => ({
                          ...current,
                          unitPrice: event.target.value,
                        }))}
                        disabled={addPositionForm.isVoid || addPositionPrefillLoading || addPositionLoading}
                      />
                    </label>
                  </div>

                  <div className="events-add-position-summary">
                    {addPositionForm.isVoid ? (
                      <span>Creates the next UUID as VOID without creating a purchase.</span>
                    ) : addPositionPrefillLoading ? (
                      <span>Loading quantity and price from the source position...</span>
                    ) : (
                      <span>
                        Creates the next UUID and a completed purchase with {addPositionForm.quantity || 0}
                        {' '}tickets at {formatUnitPrice(addPositionForm.unitPrice || 0)} each.
                      </span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary events-add-position-submit"
                    disabled={
                      addPositionLoading
                      || addPositionPrefillLoading
                      || (!addPositionForm.isVoid && !addPositionForm.userId)
                    }
                  >
                    {addPositionLoading ? 'Creating...' : 'Confirm'}
                  </button>
                </form>
              </aside>
            )}
            {adjustmentPanel && (
              <div className="events-modal-backdrop" onMouseDown={closeAdjustmentEditor}>
                <section
                  className="events-modal events-adjustment-modal"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <div className="events-modal-header">
                    <div>
                      <h2>Adjustments</h2>
                      <p>
                        <span>{adjustmentPanel.source_uuid || adjustmentPanel.event_ref || 'Position'}</span>
                        <span>{adjustmentPanel.artist || adjustmentPanel.event_name || 'Event'}</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      className="events-modal-close"
                      onClick={closeAdjustmentEditor}
                      disabled={adjustmentSaving}
                      aria-label="Close adjustments"
                    >
                      x
                    </button>
                  </div>
                  <form className="events-adjustment-form" onSubmit={handleAdjustmentSubmit}>
                    {adjustmentLoading ? (
                      <div className="events-adjustment-empty">Loading adjustments...</div>
                    ) : (
                      <>
                        <div className="events-adjustment-grid-row events-adjustment-grid-header">
                          <span>Value</span>
                          <span>Explanation</span>
                          <span aria-hidden="true"></span>
                        </div>
                        {adjustmentRows.map((row) => (
                          <div key={row.clientId} className="events-adjustment-grid-row">
                            <input
                              type="number"
                              step="0.01"
                              value={row.value}
                              onChange={(event) => setAdjustmentRows((current) => current.map((item) => (
                                item.clientId === row.clientId
                                  ? { ...item, value: event.target.value }
                                  : item
                              )))}
                              placeholder="0.00"
                              disabled={adjustmentSaving}
                              aria-label="Adjustment value"
                            />
                            <input
                              type="text"
                              value={row.explanation}
                              onChange={(event) => setAdjustmentRows((current) => current.map((item) => (
                                item.clientId === row.clientId
                                  ? { ...item, explanation: event.target.value }
                                  : item
                              )))}
                              placeholder="Explanation"
                              maxLength={1000}
                              disabled={adjustmentSaving}
                              aria-label="Adjustment explanation"
                            />
                            <button
                              type="button"
                              className="events-adjustment-remove"
                              onClick={() => setAdjustmentRows((current) => (
                                current.length === 1
                                  ? [createAdjustmentRow()]
                                  : current.filter((item) => item.clientId !== row.clientId)
                              ))}
                              disabled={adjustmentSaving}
                              aria-label="Remove adjustment"
                            >
                              x
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-secondary events-adjustment-add"
                          onClick={() => setAdjustmentRows((current) => [...current, createAdjustmentRow()])}
                          disabled={adjustmentSaving}
                        >
                          Add row
                        </button>
                        <div className="events-adjustment-total">
                          <span>Total</span>
                          <strong>
                            {formatPnlMoney(adjustmentRows.reduce((total, row) => {
                              const value = Number(row.value);
                              return total + (Number.isFinite(value) ? value : 0);
                            }, 0))}
                          </strong>
                        </div>
                      </>
                    )}
                    {adjustmentError && (
                      <div className="events-move-order-warning">{adjustmentError}</div>
                    )}
                    <div className="events-modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeAdjustmentEditor}
                        disabled={adjustmentSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={adjustmentLoading || adjustmentSaving}
                      >
                        {adjustmentSaving ? 'Saving...' : 'Save adjustments'}
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            )}
            {showCreateVenueModal && (
              <div className="events-modal-backdrop" onMouseDown={closeCreateVenueModal}>
                <section className="events-modal events-create-venue-modal" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="events-modal-header">
                    <div>
                      <h2>Add Venue</h2>
                      <p>
                        <span>Create a venue once, then select it from the Events grid.</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      className="events-modal-close"
                      onClick={closeCreateVenueModal}
                      disabled={createVenueSaving}
                    >
                      x
                    </button>
                  </div>
                  <form className="events-move-order-form" onSubmit={handleCreateVenue}>
                    <label>
                      <span>Venue Name</span>
                      <input
                        type="text"
                        value={createVenueForm.name}
                        onChange={(event) => updateCreateVenueField('name', event.target.value)}
                        disabled={createVenueSaving}
                        required
                      />
                    </label>
                    <label>
                      <span>Location</span>
                      <input
                        type="text"
                        value={createVenueForm.location}
                        onChange={(event) => updateCreateVenueField('location', event.target.value)}
                        disabled={createVenueSaving}
                        required
                      />
                    </label>
                    <label>
                      <span>Country</span>
                      <input
                        type="text"
                        value={createVenueForm.country}
                        onChange={(event) => updateCreateVenueField('country', event.target.value)}
                        disabled={createVenueSaving}
                        required
                      />
                    </label>
                    <label>
                      <span>Capacity</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={createVenueForm.capacity}
                        onChange={(event) => updateCreateVenueField('capacity', event.target.value)}
                        disabled={createVenueSaving}
                      />
                    </label>
                    <label>
                      <span>TWZ Location</span>
                      <select
                        value={createVenueForm.twz_location}
                        onChange={(event) => updateCreateVenueField('twz_location', event.target.value)}
                        disabled={createVenueSaving}
                      >
                        <option value="">Select...</option>
                        {EVENT_TWZ_LOCATION_OPTIONS.map((item) => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    </label>
                    {createVenueError && (
                      <div className="events-move-order-warning">
                        {createVenueError}
                      </div>
                    )}
                    <div className="events-modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeCreateVenueModal}
                        disabled={createVenueSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={createVenueSaving}
                      >
                        {createVenueSaving ? 'Saving...' : 'Save Venue'}
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            )}
            {addPurchasePanel && (
              <div className="events-modal-backdrop" onMouseDown={() => {
                if (!addPurchaseLoading) {
                  setAddPurchasePanel(null);
                }
              }}>
                <section className="events-modal events-add-purchase-modal" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="events-modal-header">
                    <div>
                      <h2>Add Purchase to Position</h2>
                      <p>
                        <span>{addPurchasePanel.source_uuid || addPurchasePanel.event_ref || 'Position'}</span>
                        <span>{addPurchasePanel.username || addPurchasePanel.assigned_user_raw || 'Unassigned'}</span>
                        <span>{addPurchasePanel.artist || addPurchasePanel.event_name || 'Event'}</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      className="events-modal-close"
                      onClick={() => setAddPurchasePanel(null)}
                      disabled={addPurchaseLoading}
                    >
                      x
                    </button>
                  </div>
                  <form className="events-move-order-form" onSubmit={handleAddPurchaseSubmit}>
                    <label>
                      <span>Quantity</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={addPurchaseForm.quantity}
                        onChange={(event) => setAddPurchaseForm((current) => ({
                          ...current,
                          quantity: event.target.value,
                        }))}
                        disabled={addPurchaseLoading}
                      />
                    </label>
                    <label>
                      <span>Price</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={addPurchaseForm.unitPrice}
                        onChange={(event) => setAddPurchaseForm((current) => ({
                          ...current,
                          unitPrice: event.target.value,
                        }))}
                        disabled={addPurchaseLoading}
                      />
                    </label>
                    <label>
                      <span>Card</span>
                      <input
                        type="text"
                        value={addPurchaseForm.card}
                        onChange={(event) => setAddPurchaseForm((current) => ({
                          ...current,
                          card: event.target.value,
                        }))}
                        placeholder="Optional override card"
                        disabled={addPurchaseLoading}
                      />
                    </label>
                    <div className="events-move-order-summary">
                      <span>Units will be appended to {addPurchasePanel.source_uuid || 'this position'}.</span>
                      <span>A completed manual purchase row will also be created.</span>
                    </div>
                    <div className="events-modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setAddPurchasePanel(null)}
                        disabled={addPurchaseLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={addPurchaseLoading}
                      >
                        {addPurchaseLoading ? 'Saving...' : 'Add Purchase'}
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            )}
            {moveOrderPanel && (
              <div className="events-modal-backdrop" onMouseDown={closeMoveOrder}>
                <section className="events-modal events-move-order-modal" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="events-modal-header">
                    <div>
                      <h2>Move Order</h2>
                      <p>
                        <span>{moveOrderPanel.rowData.event_name || 'Event'}</span>
                        <span>{moveOrderPanel.rowData.username || moveOrderPanel.rowData.assigned_user_raw || 'Current user'}</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      className="events-modal-close"
                      onClick={closeMoveOrder}
                      disabled={moveOrderSaving}
                    >
                      x
                    </button>
                  </div>
                  <form className="events-move-order-form" onSubmit={handleMoveOrderSubmit}>
                    <label>
                      <span>Order</span>
                      <select
                        value={moveOrderForm.orderId}
                        onChange={(event) => handleMoveOrderFieldChange('orderId', event.target.value)}
                        disabled={moveOrderSaving}
                      >
                        {moveOrderPanel.orderOptions.map((option) => (
                          <option key={option.orderId} value={option.orderId}>
                            {option.displayOrderId || option.orderId} - {option.salePoint || 'Unknown sale point'} - Qty {option.quantity}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Target position</span>
                      <select
                        value={moveOrderForm.targetUserId}
                        onChange={(event) => handleMoveOrderFieldChange('targetUserId', event.target.value)}
                        disabled={moveOrderSaving}
                      >
                        <option value="">Select position</option>
                        {moveTargetUserOptions.map((item) => (
                          <option key={item.user_id} value={item.user_id}>
                            {item.username}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selectedMoveOrder && moveOrderForm.targetUserId && (
                      <div className="events-move-order-summary">
                        <span>
                          Order quantity: {selectedMoveOrder.quantity}
                          {(selectedMoveOrder.holders || []).length > 1 && (
                            ` (${selectedMoveOrder.holders.map((holder) => `${holder.username} ${holder.quantity}`).join(' + ')})`
                          )}
                        </span>
                        {!moveToVoidSelected && moveQuantityNeeded !== selectedMoveOrder.quantity && (
                          <span>To move: {moveQuantityNeeded}</span>
                        )}
                        {moveToVoidSelected ? (
                          <span>Target: VOID / To Buy</span>
                        ) : (
                          <>
                            <span>Target available: {moveTargetAvailability.available}</span>
                            <span>Eligible slots: {moveTargetAvailability.eligible}</span>
                          </>
                        )}
                      </div>
                    )}
                    {moveOrderWarning && (
                      <div className="events-move-order-warning">
                        {moveOrderWarning}
                      </div>
                    )}
                    <div className="events-modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeMoveOrder}
                        disabled={moveOrderSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={moveOrderSaving}
                      >
                        {moveOrderSaving
                          ? 'Moving...'
                          : (moveOrderForm.allowOverAvailability ? 'Confirm Move' : 'Move Order')}
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            )}
            {contextMenu && (
              <div
                className="events-context-menu"
                style={{ left: contextMenu.x, top: contextMenu.y }}
              >
                {contextMenu.exportOnly ? null : contextMenu.eventLinkOnly ? (
                  <button
                    type="button"
                    className="events-context-menu-item"
                    onClick={() => openEventLinkEditor(contextMenu.rowData)}
                  >
                    Update primary market link
                  </button>
                ) : (
                  <>
                <button
                  type="button"
                  className="events-context-menu-item"
                  onClick={() => openTicketDetails(contextMenu.rowData)}
                >
                  Ticket Details
                </button>
                <button
                  type="button"
                  className="events-context-menu-item"
                  onClick={() => openAddPosition(contextMenu.rowData)}
                >
                  Add new position
                </button>
                <button
                  type="button"
                  className="events-context-menu-item"
                  onClick={() => openAddPurchase(contextMenu.rowData)}
                >
                  Add purchase to this position
                </button>
                <button
                  type="button"
                  className="events-context-menu-item"
                  onClick={() => openMoveOrder(contextMenu.rowData)}
                >
                  Move Order
                </button>
                <button
                  type="button"
                  className="events-context-menu-item"
                  onClick={() => openUserHistory(contextMenu.rowData)}
                >
                  User History
                </button>
                  </>
                )}
                {canExport && (
                <button
                  type="button"
                  className="events-context-menu-item events-context-menu-parent"
                  onClick={(event) => {
                    event.stopPropagation();
                    setContextMenu((current) => (current ? { ...current, exportExpanded: !current.exportExpanded } : current));
                  }}
                >
                  Export {contextMenu.exportExpanded ? '▾' : '▸'}
                </button>
                )}
                {canExport && contextMenu.exportExpanded && (
                  <>
                    <button
                      type="button"
                      className="events-context-menu-item events-context-menu-subitem"
                      onClick={handleExportCsv}
                    >
                      CSV
                    </button>
                    <button
                      type="button"
                      className="events-context-menu-item events-context-menu-subitem"
                      onClick={handleExportFullCsv}
                    >
                      Full Data CSV
                    </button>
                    <button
                      type="button"
                      className="events-context-menu-item events-context-menu-subitem"
                      onClick={handleExportShopifyCsv}
                    >
                      Shopify CSV
                    </button>
                  </>
                )}
              </div>
            )}
            {eventLinkPanel && (
              <div className="events-modal-backdrop" onMouseDown={closeEventLinkEditor}>
                <section className="events-modal" onMouseDown={(event) => event.stopPropagation()}>
                  <div className="events-modal-header">
                    <div>
                      <h2>Primary market link</h2>
                      <p>{eventLinkPanel.artist || eventLinkPanel.event_name || 'Event'}</p>
                    </div>
                    <button
                      type="button"
                      className="events-modal-close"
                      onClick={closeEventLinkEditor}
                      disabled={eventLinkSaving}
                      aria-label="Close"
                    >
                      x
                    </button>
                  </div>
                  <form className="events-move-order-form" onSubmit={handleEventLinkSubmit}>
                    <label>
                      <span>Link</span>
                      <input
                        type="url"
                        value={eventLinkValue}
                        onChange={(event) => setEventLinkValue(event.target.value)}
                        placeholder="https://example.com/event"
                        maxLength={2048}
                        autoFocus
                      />
                    </label>
                    {eventLinkError && <div className="events-move-order-warning">{eventLinkError}</div>}
                    <div className="events-modal-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeEventLinkEditor}
                        disabled={eventLinkSaving}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={eventLinkSaving}>
                        {eventLinkSaving ? 'Saving...' : 'Save link'}
                      </button>
                    </div>
                  </form>
                </section>
              </div>
            )}
          </div>
          </div>
        )}
      </main>
    </div>
  );
};

export { calculateUnitViewPnl, createPnlAccumulator, addUnitToPnl, mergePnlSummary, finalizePnlSummary, averageUnitBuy };
export default EventsDisplayPage;
